'use client'

/**
 * @file Catalyst.tsx
 * @desc Floating AI chat widget for the WFW marketing and admin surfaces.
 *
 * STATE MACHINE:
 *   collapsed (default on page load)
 *     -> user clicks "Start a Chat"
 *   expanded (tall panel, docked bottom-right)
 *     -> user clicks the floating close button or the "x" in the header
 *   collapsed
 *
 * API CONTRACTS:
 *   POST /api/catalyst/chat
 *     Request:  { sessionId, message, email?, partnerSlug?, conversationHistory? }
 *     Response: SSE stream
 *       - delta events:  data: { delta: string }
 *       - final event:   data: { done: true, sources: Source[], messageId: string, isEscalation: boolean }
 *       - error event:   data: { error: string }
 *
 *   POST /api/catalyst/crm
 *     Request:  { sessionId, email, firstName?, lastName?, company?, message?,
 *                 conversationSummary?, escalationTrigger?, partnerSlug? }
 *     Response: Always 200 { ok: true }
 *
 *   GET /api/voice-widget/config/[agentId]
 *     Response: { enabled: false } | { enabled: true, agentName, buttonText, firstMessage,
 *               color, avatarUrl, allowedOrigins, defaultMode: 'chat' | 'voice' }
 *     defaultMode: sourced from partner_config.chatbot_default_mode (Wave 1 / migration 0062).
 *     When 'voice', the collapsed bubble launches a live call on click instead of opening
 *     the chat panel. Falls back to 'chat' when the field is absent or enabled=false.
 *
 *   GET /api/voice-widget/signed-url?agent_id=<agentId>
 *     Response: { signedUrl, expiresAt } | 403/429/500 with { error }
 *
 * BRAND AWARENESS:
 *   Receives a resolved ResolvedBrand prop from the server layout.
 *   Uses brand.colors.accent, brand.name, brand.logo.icon,
 *   brand.catalyst.agentName, and brand.cta.href.
 *   Passes partnerSlug to both API routes so multi-tenant KB lookup works.
 *
 * EVENTS LISTENED:
 *   "catalyst:open"        — any page can programmatically open the widget
 *                            e.g. window.dispatchEvent(new Event('catalyst:open'))
 *                            Wave 5c: if defaultMode='voice' (and voice enabled),
 *                            this also launches a live call, so the voice-first
 *                            setting governs external CTAs, not just the bubble.
 *   "catalyst:open-voice"  — open the widget AND start a live voice call now,
 *                            regardless of defaultMode. Explicit voice launcher
 *                            for host-page buttons (e.g. Teamphoria "Talk to Linda").
 *                            e.g. window.dispatchEvent(new Event('catalyst:open-voice'))
 *
 * LOCALSTORAGE KEYS:
 *   "catalyst.session"     — persists { sessionId, messages, userTurns, capturedEmail,
 *                            activeEscalation } across page reloads
 *
 * CRM TRIGGER CONDITIONS:
 *   1. Widget close (if conversation has more than 1 message and CRM not yet saved)
 *   2. 5-minute inactivity timer reset on every new message
 *   3. Email capture form submission
 *   4. Escalation CTA email-capture form submission
 *   5. Voice call end (always fires with conversationSummary='Voice call')
 *
 * VOICE MODE (Phase 4):
 *   On widget mount, one config check fires to GET /api/voice-widget/config/{agentId}.
 *   If enabled=false or the request fails, the "Call Catalyst" button is hidden.
 *   If enabled=true, clicking the button:
 *     1. Sets voiceMode='requesting' (shows spinner + "Connecting...")
 *     2. Fetches a signed WebSocket URL from GET /api/voice-widget/signed-url
 *     3. On success, sets voiceMode='active' and renders the in-panel voice UI
 *     4. Mounts <elevenlabs-convai signed-url="..."> in a hidden div so the
 *        ElevenLabs machinery runs without rendering its own floating button.
 *   Voice UI renders inside the chat panel body, replacing the message thread.
 *   Every call exit (end, back to text, close, tab hide, remote hang-up) writes the
 *   spoken turns as `voice-turn` rows plus one `call-status` row (#1888). Only real
 *   turns reach the model; see src/lib/catalyst/widgetConversation.ts.
 *
 * VOICE DEFAULT MODE (Wave 5b):
 *   When defaultMode='voice' AND enabled=true (sourced from partner_config.chatbot_default_mode),
 *   the collapsed bubble's onClick skips the chat panel entirely and calls handleCallCatalyst()
 *   directly. The bubble icon changes from a chat/sparkle icon to a MicIcon, and the label
 *   changes from "Start a Chat" to "Talk to Catalyst" to signal the voice-first intent.
 *   Fallbacks:
 *     - If config has not yet loaded (voiceEnabled===null), bubble behaves as chat-default
 *       to avoid a race-condition where the call starts before the signed-url check completes.
 *     - If defaultMode='chat' or absent, current behavior is preserved exactly.
 *     - If the voice start FAILS, voiceMode='error' surfaces the exact error inline.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { ConversationProvider, useConversation } from '@elevenlabs/react'
import type { ResolvedBrand } from '@/lib/brand/resolveBrand'
import { LANGUAGE_DISPLAY_NAMES, isValidLanguageCode } from '@/lib/i18n/widget-languages'
import {
  GREETING_ID,
  buildCallRecord,
  buildModelHistory,
  normaliseStoredMessages,
  resolveGreeting,
  type WidgetMessage,
  type WidgetSource,
} from '@/lib/catalyst/widgetConversation'
import { SpamHoneypots } from '@/app/marketing/_components/SpamHoneypots'

// ── API base origin ─────────────────────────────────────────────────────────
//
// Origin prefix for every Catalyst backend call (config, signed-url, chat, crm,
// voice/debug). Empty string => relative URLs (same-origin), which is correct
// for the in-app dashboard and the marketing site, where Catalyst is served by
// the WFW backend itself. Those callers never set this, so behavior is unchanged.
//
// The embed bundle (src/embed/agent.tsx) calls setCatalystApiBase() once, before
// the widget mounts, with the origin the embed <script> was loaded from
// (e.g. https://www.workforcewave.com). That lets a widget pasted on an
// independent third-party site (e.g. codeandtrust.com) reach the WFW backend
// cross-origin instead of the host page's own origin, which has no /api routes.
//
// Module-scoped (not a prop) because saveToCrm() is a standalone helper outside
// the component tree, and there is only ever one Catalyst widget per page, set
// once at mount -- so a single shared value is correct and avoids threading a
// prop through four component layers and five saveToCrm call sites.
let catalystApiBase = ''

// Widget mount time, sent as `formRenderedAt` on every CRM post so the
// server-side time-trap has a real value. Module-scoped for the same reason as
// catalystApiBase: saveToCrm() lives outside the component tree.
const catalystMountedAt = Date.now()

/**
 * Set the API base origin for all Catalyst backend calls. Called once by the
 * embed bundle before the widget renders. No-op in same-origin contexts (the
 * default empty string is used). Trailing slashes are stripped so
 * `${base}${path}` never yields a double slash.
 */
export function setCatalystApiBase(base: string): void {
  catalystApiBase = base.replace(/\/+$/, '')
}

// ── ID generator ──────────────────────────────────────────────────────────────

/**
 * Generates a unique ID for messages and sessions.
 * Uses crypto.randomUUID() when available (secure contexts / HTTPS).
 * Falls back to Math.random() for HTTP dev environments.
 */
function genId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as Crypto).randomUUID === 'function'
  ) {
    return (crypto as Crypto).randomUUID()
  }
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36)
}

// ── Types ─────────────────────────────────────────────────────────────────────

/** A KB source returned by the chat API and rendered as citation chips. */
type Source = WidgetSource

/** A single message in the widget thread; see `widgetConversation.ts` for `kind`. */
type Message = WidgetMessage

/** Escalation type labels (subset of what the API detects). */
type EscalationTrigger = 'pricing' | 'comparison' | 'timeline' | 'demo' | null

/** Theme object derived from the resolved brand, used by sub-components. */
interface CatalystTheme {
  accent: string
  accentDark: string
  brandName: string
  ctaHref: string
  partnerSlug: string
}

/** Serializable subset persisted in localStorage. */
interface PersistedSession {
  sessionId: string
  messages: Message[]
  userTurns: number
  capturedEmail: string | null
  activeEscalation: EscalationTrigger
}

/**
 * Voice mode state machine for the in-panel voice UI.
 * idle       - chat view, voice button visible (if enabled)
 * requesting - waiting for signed-url fetch (spinner shown)
 * active     - convai element mounted, voice UI visible
 * error      - fetch failed, exact error shown inline
 */
type VoiceMode = 'idle' | 'requesting' | 'active' | 'error'

/**
 * Config response from GET /api/voice-widget/config/[agentId].
 * When enabled=false the shape only carries that flag.
 * defaultMode is only present in the enabled=true branch (additive, Wave 5b).
 */
interface VoiceConfig {
  enabled: boolean
  agentName?: string
  buttonText?: string
  firstMessage?: string
  color?: string
  avatarUrl?: string
  allowedOrigins?: string[]
  /**
   * Controls how the collapsed bubble behaves on click (Wave 5b).
   * 'voice' -- click triggers a live call immediately, bypassing the chat panel.
   * 'chat'  -- click expands the chat panel (default/current behavior).
   * Absent when enabled=false; treated as 'chat' when absent or undefined.
   */
  defaultMode?: 'voice' | 'chat'
}

/**
 * A single turn in the live voice transcript shown during an active call.
 * Speaker is 'user' or 'agent'; text accumulates as the convai element fires events.
 */
interface VoiceTurn {
  id: string
  speaker: 'user' | 'agent'
  text: string
  timestamp: string
}

// ── LocalStorage helpers ──────────────────────────────────────────────────────

const SESSION_KEY = 'catalyst.session'

/**
 * Reads the persisted session from localStorage.
 * Returns null on parse failure or if no session exists.
 * Wrapped in try/catch because localStorage can throw in some browser
 * privacy modes or when storage is full.
 */
function loadSession(): PersistedSession | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedSession
    return { ...parsed, messages: normaliseStoredMessages(parsed.messages ?? []) }
  } catch {
    return null
  }
}

/**
 * Writes the current session state to localStorage.
 * Silently ignores write errors (storage quota exceeded, etc.).
 */
function saveSession(session: PersistedSession): void {
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    // Non-critical, never throw
  }
}

// ── Escalation helpers ────────────────────────────────────────────────────────

/**
 * Returns messaging copy for each escalation trigger type.
 * Used in the EscalationCard and EscalationEmailCapture sub-components.
 */
function buildEscalationMessages(
  brandName: string
): Record<string, { headline: string; body: string }> {
  return {
    pricing: {
      headline: 'Get a Custom Pricing Quote',
      body: `Pricing depends on your call volume, team size, and use case. Let's build a plan that fits your budget.`,
    },
    comparison: {
      headline: 'See a Head-to-Head Demo',
      body: `The best way to compare is to see ${brandName} in action for your specific industry. 30 minutes, no pressure.`,
    },
    timeline: {
      headline: 'Implementation in Under 30 Days',
      body: `Most clients are live in 2-4 weeks with our white-glove onboarding team. Let's map your timeline.`,
    },
    demo: {
      headline: 'Book a Live Demo',
      body: `See ${brandName}'s AI voice agents in action on a live call. No deck, no pressure.`,
    },
  }
}

// ── CRM helper ────────────────────────────────────────────────────────────────

/**
 * Fire-and-forget CRM pipeline call.
 * Sends the current session state to /api/catalyst/crm.
 * Never throws, never retries. Uses keepalive so the request survives
 * page unload (e.g. tab close triggers via widget close handler).
 */
async function saveToCrm(data: {
  sessionId: string
  email?: string
  messages: { role: string; content: string }[]
  escalationTrigger?: string
  partnerSlug?: string
  conversationSummary?: string
  website?: string
  nickname?: string
}): Promise<void> {
  try {
    // Build conversationSummary from the last 2 user messages when not provided
    const summary =
      data.conversationSummary ??
      data.messages
        .filter((m) => m.role === 'user')
        .slice(-2)
        .map((m) => m.content)
        .join(' | ')

    await fetch(`${catalystApiBase}/api/catalyst/crm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: data.sessionId,
        email: data.email,
        partnerSlug: data.partnerSlug ?? 'workforcewave',
        conversationSummary: summary,
        escalationTrigger: data.escalationTrigger,
        // Anti-spam layers the server evaluates. The honeypots only carry a
        // value on the escalation card, the one trigger with a real form; the
        // machine-fired triggers send empty slots.
        website: data.website ?? '',
        nickname: data.nickname ?? '',
        formRenderedAt: catalystMountedAt,
      }),
      keepalive: true,
    })
  } catch {
    // Non-critical, never surface to user
  }
}

// ── Icons ─────────────────────────────────────────────────────────────────────

/** Sparkle/star icon shown in the agent avatar tile and on the floating button. */
function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74L12 2z" />
    </svg>
  )
}

/** X (close) icon for the header and the floating close disc. */
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

/** Paper-airplane send icon for the input bar submit button. */
function SendIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
    </svg>
  )
}

/** Phone icon for the "Call Catalyst" button in the header. */
function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.08 2.2 2 2 0 012.08 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92z" />
    </svg>
  )
}

/** Microphone icon shown on the mute-toggle button during an active voice call. */
function MicIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M19 10a7 7 0 01-14 0M12 19v3M8 22h8" />
    </svg>
  )
}

/** Microphone-off icon shown when the user is muted during a voice call. */
function MicOffIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M18.89 13.23A7 7 0 0019 10M15 9.34V4a3 3 0 00-5.68-1.33M9 9v3a3 3 0 005.12 2.12" />
      <path d="M17 16.95A7 7 0 015 10v-1M12 19v3M8 22h8" />
    </svg>
  )
}

/** Phone-down (end-call) icon for the end-call button in voice mode. */
function PhoneOffIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.42 19.42 0 01-3.33-2.67m-2.67-3.34A19.79 19.79 0 012 4.2 2 2 0 013.72 2H7a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  )
}

/** Simple spinner used during voice connection handshake. */
function SpinnerIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        d="M12 2a10 10 0 0110 10"
        strokeLinecap="round"
      />
    </svg>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Visible keyboard focus style for widget buttons. */
const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500'

/**
 * One muted status line per call, with that call's spoken turns in a
 * collapsed disclosure (#1888). Not an assistant bubble: the status is UI
 * state, not something the agent said.
 */
function CallStatusRow({
  status,
  turns,
  agentName,
}: {
  status: string
  turns: WidgetMessage[]
  agentName: string
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
        <PhoneIcon className="w-3 h-3" />
        <span>{status}</span>
      </p>
      {turns.length > 0 && (
        <details className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
          <summary className={`cursor-pointer select-none text-gray-500 rounded-sm ${FOCUS_RING}`}>
            Call transcript
          </summary>
          <ul className="mt-2 space-y-1.5">
            {turns.map((turn) => (
              <li key={turn.id} className="leading-relaxed">
                <span className="font-semibold text-gray-900">
                  {turn.role === 'user' ? 'You' : agentName}:
                </span>{' '}
                {turn.content}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

/** Animated three-dot typing indicator shown while the assistant is streaming. */
function TypingIndicator({ theme }: { theme: CatalystTheme }) {
  return (
    <div className="flex items-center gap-1 px-3 py-2.5 rounded-2xl rounded-bl-sm bg-white border border-gray-200 shadow-sm w-fit">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 rounded-full animate-bounce"
          style={{
            backgroundColor: theme.accent,
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  )
}

/**
 * Renders source citation chips below an assistant message.
 * Shows at most 3 chips to avoid overflowing the message area.
 * Each chip links to the source URL.
 */
function SourceChips({
  sources,
  theme,
}: {
  sources: Source[]
  theme: CatalystTheme
}) {
  if (sources.length === 0) return null

  // Human-readable label for each source type
  const labelMap: Record<string, string> = {
    'case-study': 'Case Study',
    blog: 'Blog',
    feature: 'Feature',
    page: 'Page',
    pricing: 'Pricing',
    integration: 'Integration',
    service: 'Service',
    support: 'Support',
  }

  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {sources.slice(0, 3).map((s, i) => (
        <a
          key={i}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors hover:opacity-80"
          style={{
            backgroundColor: `${theme.accent}18`,
            color: theme.accent,
            borderColor: `${theme.accent}40`,
          }}
        >
          <span className="opacity-60">{labelMap[s.type] ?? s.type}</span>
          <span className="truncate max-w-[100px]">{s.title}</span>
        </a>
      ))}
    </div>
  )
}

/**
 * Escalation CTA card shown after an assistant message that triggered an
 * escalation (pricing, comparison, timeline, or demo intent detected).
 * Contains a "Want to see this in action?" prompt + email input + "Book a Demo"
 * button. On submit, fires POST /api/catalyst/crm with escalation context.
 */
function EscalationCard({
  trigger,
  theme,
  sessionId,
  messages,
  onEmailCaptured,
}: {
  trigger: string
  theme: CatalystTheme
  sessionId: string
  messages: { role: string; content: string }[]
  onEmailCaptured: (email: string) => void
}) {
  const info =
    buildEscalationMessages(theme.brandName)[trigger] ??
    buildEscalationMessages(theme.brandName).pricing
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // Dual honeypot — off-screen fields that must stay empty for a real visitor.
  const [website, setWebsite] = useState('')
  const [nickname, setNickname] = useState('')

  /** Validates the email and fires the CRM route with escalation context. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) {
      setError('Please enter a valid email address.')
      return
    }
    setError('')
    setSubmitting(true)
    try {
      await saveToCrm({
        sessionId,
        email,
        messages,
        escalationTrigger: trigger,
        partnerSlug: theme.partnerSlug,
        website,
        nickname,
        conversationSummary: `Escalation trigger: ${trigger}. ${messages
          .filter((m) => m.role === 'user')
          .slice(-2)
          .map((m) => m.content)
          .join(' | ')}`,
      })
      onEmailCaptured(email)
      setSubmitted(true)
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="rounded-xl border p-4 mt-2"
      style={{
        backgroundColor: `${theme.accent}12`,
        borderColor: `${theme.accent}40`,
      }}
    >
      <p
        className="text-xs font-bold uppercase tracking-wider mb-1"
        style={{ color: theme.accent }}
      >
        Want to see this in action?
      </p>
      <p className="text-sm font-semibold text-gray-800 mb-1">{info.headline}</p>
      <p className="text-xs text-gray-500 mb-3">{info.body}</p>

      {submitted ? (
        // Thank-you state shown after a successful CRM submission
        <p className="text-sm font-medium" style={{ color: theme.accent }}>
          Got it! Our team will reach out shortly.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-2">
          <SpamHoneypots
            website={website}
            onWebsite={setWebsite}
            nickname={nickname}
            onNickname={setNickname}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none"
            style={{ borderColor: `${theme.accent}60` }}
            autoFocus={false}
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg px-3 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: theme.accent }}
          >
            {submitting ? 'Booking...' : 'Book a Demo'}
          </button>
        </form>
      )}
    </div>
  )
}

/**
 * Inline email capture form shown as an assistant-style message after
 * the user's second turn (when no email has been collected yet).
 * On submit: fires CRM, dismisses the form, confirms via message.
 * On dismiss: hides without capturing.
 */
function EmailCapturePrompt({
  onSubmit,
  onDismiss,
  theme,
}: {
  onSubmit: (email: string) => void
  onDismiss: () => void
  theme: CatalystTheme
}) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  /** Validates the email and calls the parent's submit handler. */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) {
      setError('Please enter a valid email address.')
      return
    }
    onSubmit(email)
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p
        className="text-xs font-bold uppercase tracking-wider mb-1"
        style={{ color: theme.accent }}
      >
        Stay in touch
      </p>
      <p className="text-sm font-semibold text-gray-800 mb-1">
        Want me to send you a recap?
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Drop your email and we&apos;ll send you relevant resources and answer any
        follow-up questions.
      </p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none"
          style={{ borderColor: `${theme.accent}60` }}
          autoFocus
        />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2">
          <button
            type="submit"
            className="flex-1 rounded-lg px-3 py-2 text-xs font-bold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: theme.accent }}
          >
            Send
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
          >
            Skip
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Voice sub-components ──────────────────────────────────────────────────────

/**
 * Animated audio orb shown in the center of the voice mode UI.
 * Pulses continuously while the call is active to signal that audio is live.
 * Color matches the brand accent so it visually blends with the widget theme.
 */
function VoiceOrb({ color, status }: { color: string; status: string }) {
  return (
    <div className="flex flex-col items-center gap-3">
      {/* Outer glow ring — large, low-opacity pulse */}
      <div className="relative flex items-center justify-center">
        <span
          className="absolute w-24 h-24 rounded-full animate-ping opacity-20"
          style={{ backgroundColor: color }}
        />
        {/* Inner solid orb */}
        <div
          className="relative z-10 w-20 h-20 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: `radial-gradient(circle at 35% 35%, ${color}cc, ${color})` }}
        >
          <MicIcon className="w-8 h-8 text-white" />
        </div>
      </div>
      {/* Status label below the orb */}
      <p className="text-sm font-semibold text-gray-700">{status}</p>
    </div>
  )
}

/**
 * Scrolling live transcript area shown beneath the voice orb.
 * Displays the last 4 turns with speaker labels and timestamps.
 * Each turn is color-coded: user turns on the right, agent on the left.
 */
function VoiceTranscript({
  turns,
  accentColor,
}: {
  turns: VoiceTurn[]
  accentColor: string
}) {
  const transcriptEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom as new transcript turns appear
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns])

  if (turns.length === 0) {
    return (
      <div className="w-full text-center text-xs text-gray-400 py-2">
        Speak to start the conversation...
      </div>
    )
  }

  return (
    <div className="w-full max-h-44 overflow-y-auto space-y-2 px-1">
      {turns.slice(-4).map((turn) => (
        <div
          key={turn.id}
          className={`flex flex-col gap-0.5 ${turn.speaker === 'user' ? 'items-end' : 'items-start'}`}
        >
          {/* Speaker label + timestamp */}
          <span className="text-[9px] text-gray-400 uppercase tracking-wider">
            {turn.speaker === 'user' ? 'You' : 'Catalyst'} &middot; {turn.timestamp}
          </span>
          {/* Transcript bubble */}
          <div
            className="max-w-[90%] px-2.5 py-1.5 rounded-xl text-xs leading-relaxed"
            style={
              turn.speaker === 'user'
                ? { backgroundColor: accentColor, color: '#fff' }
                : { backgroundColor: '#f3f4f6', color: '#374151' }
            }
          >
            {turn.text}
          </div>
        </div>
      ))}
      <div ref={transcriptEndRef} />
    </div>
  )
}

/**
 * Bottom control row for the active voice call.
 * Contains: mute toggle (left), end-call (center-right), back-to-chat link (right).
 * All callbacks are passed from the parent to keep state management in one place.
 */
function VoiceControls({
  isMuted,
  onToggleMute,
  onEndCall,
  onBackToChat,
  accentColor,
}: {
  isMuted: boolean
  onToggleMute: () => void
  onEndCall: () => void
  onBackToChat: () => void
  accentColor: string
}) {
  return (
    <div className="flex items-center justify-between w-full px-2">
      {/* Mute toggle button */}
      <button
        type="button"
        onClick={onToggleMute}
        className="flex flex-col items-center gap-1 p-2 rounded-xl transition-colors hover:bg-gray-100"
        title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? (
          <MicOffIcon className="w-5 h-5 text-red-500" />
        ) : (
          <MicIcon className="w-5 h-5" style={{ color: accentColor }} />
        )}
        <span className="text-[9px] text-gray-500">{isMuted ? 'Unmuted' : 'Mute'}</span>
      </button>

      {/* End-call button (prominent red) */}
      <button
        type="button"
        onClick={onEndCall}
        className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-colors"
        title="End voice call"
        aria-label="End call"
      >
        <PhoneOffIcon className="w-5 h-5" />
      </button>

      {/* Back to chat text link */}
      <button
        type="button"
        onClick={onBackToChat}
        className="text-[10px] text-gray-400 hover:text-gray-600 underline transition-colors"
      >
        Back to chat
      </button>
    </div>
  )
}

// ── Main Widget ───────────────────────────────────────────────────────────────

export interface CatalystProps {
  /** Resolved brand config passed from the server layout. */
  brand: ResolvedBrand
  /**
   * ISO 639-1 language codes the widget should support.
   * When more than one code is provided, a language dropdown appears in the header.
   * The selected language is passed as a dynamic variable to both voice and chat APIs
   * so the agent responds in the right language.
   * Default: ['en']
   */
  languages?: string[]
  /**
   * Interaction modes enabled for this widget instance.
   * 'voice' -- show the Call button and allow voice sessions
   * 'text'  -- show the chat input and allow text conversations
   * When only 'voice' is provided, the chat UI is hidden and clicking the pill
   * goes straight into a voice call. When only 'text', the Call button is hidden.
   * Default: ['voice', 'text']
   */
  modes?: ('voice' | 'text')[]
  /**
   * When true, the collapsed floating launcher (the bottom-right pill/FAB) is NOT
   * rendered. The widget still mounts and still responds to the `catalyst:open`
   * and `catalyst:open-voice` window events, so a host page can drive it entirely
   * from its OWN call-to-action (e.g. an inline "Talk to us" button) without a
   * second floating launcher appearing. Opening the widget shows the panel as
   * normal; closing it returns to nothing (no pill). Default: false (launcher shown).
   */
  hideLauncher?: boolean
}

/**
 * Floating AI chat widget.
 *
 * Receives a brand prop from the server layout wrapper (CatalystServerMount
 * or the layout itself). Manages its own state machine between collapsed and
 * expanded, persists session to localStorage, and calls the Phase 2 APIs.
 */
/**
 * Public Catalyst component. Wraps the inner widget with ConversationProvider
 * so the @elevenlabs/react useConversation() hook can register its callbacks
 * with the provider context. Without this wrapper the hook throws
 * "useRegisterCallbacks must be used within a ConversationProvider" at first
 * render and the entire widget crashes.
 */
export default function Catalyst({ brand, languages, modes, hideLauncher }: CatalystProps) {

  return (
    <ConversationProvider>
      <CatalystInner brand={brand} languages={languages} modes={modes} hideLauncher={hideLauncher} />
    </ConversationProvider>
  )
}

/**
 * Inner Catalyst widget. All hooks (state, effects, useConversation) live here.
 * The outer Catalyst export only exists to provide the ConversationProvider.
 */
function CatalystInner({ brand, languages = ['en'], modes = ['voice', 'text'], hideLauncher = false }: CatalystProps) {
  // Derived mode flags used to conditionally show/hide voice and text UI elements.
  // voiceModeEnabled: true when 'voice' is in the modes array.
  // textModeEnabled:  true when 'text' is in the modes array.
  const voiceModeEnabled = modes.includes('voice')
  const textModeEnabled = modes.includes('text')

  // Active language for this session. Defaults to the first supported language.
  // When languages.length > 1 a dropdown in the header lets the visitor switch.
  const [selectedLanguage, setSelectedLanguage] = useState<string>(languages[0] ?? 'en')
  // ── Mount gate (avoid SSR/client hydration mismatch) ─────────────────────
  // The widget reads localStorage and calls genId() during state init, both
  // of which produce different values on server vs client. To prevent React
  // from refusing to hydrate the entire subtree (which makes the widget look
  // rendered but be totally non-interactive), we render nothing on SSR and
  // first-pass hydration, then flip mounted=true on the client and render
  // the real widget. The widget appears one tick after page load — fine for
  // a chat surface, no SEO impact.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    // Standard SSR-safe mount flag. React batches this single setState in the
    // first commit, so the rule's cascading-render concern doesn't apply.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true)
  }, [])

  // ── Derived constants ────────────────────────────────────────────────────

  const agentName = brand.catalyst.agentName || 'Catalyst'

  const theme: CatalystTheme = {
    accent: brand.colors.accent,
    accentDark: brand.colors.accentDark,
    brandName: brand.name,
    ctaHref: brand.cta.href,
    partnerSlug: brand.slug,
  }

  // ── State ────────────────────────────────────────────────────────────────

  const [isOpen, setIsOpen] = useState(false)

  // Session state — all lazy-initialized from localStorage on first render.
  // Using the lazy initializer form of useState avoids calling setState()
  // inside a useEffect (which the lint rule react-hooks/set-state-in-effect flags).
  // The initializer only runs once, client-side, so SSR sees empty defaults.

  const [sessionId, setSessionId] = useState<string>(() => {
    if (typeof window === 'undefined') return ''
    return loadSession()?.sessionId ?? genId()
  })

  const [messages, setMessages] = useState<Message[]>(() => {
    if (typeof window === 'undefined') return []
    return loadSession()?.messages ?? []
  })

  const [userTurns, setUserTurns] = useState<number>(() => {
    if (typeof window === 'undefined') return 0
    return loadSession()?.userTurns ?? 0
  })

  const [capturedEmail, setCapturedEmail] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return loadSession()?.capturedEmail ?? null
  })

  const [activeEscalation, setActiveEscalation] =
    useState<EscalationTrigger>(() => {
      if (typeof window === 'undefined') return null
      return loadSession()?.activeEscalation ?? null
    })

  // hasOpened tracks whether the greeting has been shown (not persisted).
  // Derived from whether there are already messages in the restored session.
  const [hasOpened, setHasOpened] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    const saved = loadSession()
    return saved ? saved.messages.length > 0 : false
  })

  // UI-only state (not persisted)
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [crmSaved, setCrmSaved] = useState(false)

  // ── Voice mode state ─────────────────────────────────────────────────────

  /**
   * voiceEnabled: tri-state.
   *   null  = config check not yet complete (button hidden during load)
   *   false = agent has web_widget_enabled=false (button hidden permanently)
   *   true  = agent is web-enabled (button shown)
   */
  const [voiceEnabled, setVoiceEnabled] = useState<boolean | null>(null)

  /** Config fetched from /api/voice-widget/config/[agentId] when enabled=true. */
  const [voiceConfig, setVoiceConfig] = useState<VoiceConfig | null>(null)

  /**
   * Greeting text. The greeting row keeps id 'greeting' and renders once; its
   * text resolves at render time, so a late config load updates it in place.
   * The fallback is generic and names the assistant as AI (#1888).
   */
  const greetingText = resolveGreeting(voiceConfig?.firstMessage, agentName, brand.name)

  /**
   * The defaultMode returned by the config endpoint (Wave 5b).
   * 'chat' (default) -- bubble opens the chat panel on click.
   * 'voice'          -- bubble triggers a live call immediately on click.
   * Stays 'chat' until the config check completes and returns 'voice' explicitly.
   * This default prevents a race condition where the user clicks the bubble before
   * the config check has returned -- we never pre-empt into voice mode blind.
   */
  const [voiceDefaultMode, setVoiceDefaultMode] = useState<'voice' | 'chat'>('chat')

  /** Current phase of the voice mode state machine. */
  const [voiceMode, setVoiceMode] = useState<VoiceMode>('idle')

  /**
   * Derived gate: true when the collapsed bubble should skip the chat panel and
   * launch a voice call directly on click (Wave 5b).
   *
   * All three conditions must hold:
   *   1. voiceDefaultMode='voice' -- the admin explicitly chose voice-first UX.
   *   2. voiceEnabled=true        -- the config check succeeded and enabled=true.
   *   3. voiceModeEnabled=true    -- 'voice' is in the modes[] prop (not overridden by host).
   *
   * We do NOT include a configLoaded check here because voiceEnabled acts as the
   * authoritative loaded signal: it stays null until the fetch completes, false on error
   * or disabled, and true only on a successful enabled=true response. So when
   * voiceEnabled===null (still loading), shouldVoiceDefault is always false, which
   * prevents pre-emptive voice launch before we know the config.
   */
  const shouldVoiceDefault =
    voiceDefaultMode === 'voice' && voiceEnabled === true && voiceModeEnabled

  /** Exact error string shown inline when voiceMode='error'. */
  const [voiceError, setVoiceError] = useState('')

  /** Current status text shown below the audio orb during an active call. */
  const [voiceStatus, setVoiceStatus] = useState('Connecting...')

  /** Live transcript turns accumulated from convai element events. */
  const [voiceTranscript, setVoiceTranscript] = useState<VoiceTurn[]>([])

  /** Mirrors voiceTranscript so window listeners read the current call's turns. */
  const voiceTranscriptRef = useRef<VoiceTurn[]>([])
  /** Id of the call in progress. Cleared once its record is in the thread. */
  const callIdRef = useRef<string | null>(null)
  /** True once the SDK reports that the current call connected. */
  const callConnectedRef = useRef(false)
  /** Set on a return to text so the composer takes focus once it mounts. */
  const focusComposerRef = useRef(false)
  /** Last call-status text, announced by the status region while idle. */
  const [callEndedNotice, setCallEndedNotice] = useState('')

  /**
   * Writes a finished call into the thread once: each spoken turn as a
   * `voice-turn` row, then one `call-status` row (#1888). Every exit path calls
   * this (end call, back to text, close, tab hide, remote hang-up). The callId
   * guard makes a second call for the same call a no-op.
   */
  const finishCall = useCallback((reason: 'ended' | 'hidden') => {
    const callId = callIdRef.current
    if (!callId) return
    callIdRef.current = null
    const turns = voiceTranscriptRef.current
    const connected = callConnectedRef.current
    callConnectedRef.current = false
    // A start abandoned before it connected leaves no row, unless the tab hid it.
    if (!connected && turns.length === 0 && reason !== 'hidden') return
    const status =
      reason === 'hidden'
        ? 'Call ended because this tab was hidden.'
        : `Voice call ended at ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`
    setMessages((prev) => [...prev, ...buildCallRecord(callId, turns, status, genId)])
    setCallEndedNotice(status)
  }, [])

  /** Whether the user's mic input is currently muted. */
  const [isMuted, setIsMuted] = useState(false)

  // ── Voice diagnostics state ──────────────────────────────────────────────
  // Real-time observability for the voice flow. Built so we can see exactly
  // what the convai element is doing, what the mic is hearing, and where any
  // failure occurs. Toggle visibility with the bug button in voice mode.

  /** Last 30 events fired by the <elevenlabs-convai> element, newest last. */
  const [voiceEvents, setVoiceEvents] = useState<
    Array<{ id: string; type: string; detail: string; ts: string }>
  >([])

  /** Live mic input level (0-100) from the AudioContext analyser. */
  const [micLevel, setMicLevel] = useState(0)

  /** Mic permission / capture state. */
  const [micState, setMicState] = useState<'idle' | 'requesting' | 'granted' | 'denied' | 'error'>(
    'idle'
  )

  /** Whether the troubleshooting panel (mic/output meters, waveforms, test
   *  buttons, event log) is visible inside voice mode. Hidden by default so
   *  ordinary callers see only the orb, transcript, and call controls. */
  const [debugOpen, setDebugOpen] = useState(false)

  /** Mic test recorder state. Tracks the record-then-playback flow:
   *  idle -> recording -> playing -> idle. Used by the "Test microphone"
   *  button inside the troubleshooting panel. */
  const [micTestState, setMicTestState] = useState<'idle' | 'recording' | 'playing'>(
    'idle'
  )

  /** Refs for the live waveform canvases drawn from the SDK's input and
   *  output frequency data. Populated when the troubleshooting panel mounts. */
  const inputWaveformRef = useRef<HTMLCanvasElement | null>(null)
  const outputWaveformRef = useRef<HTMLCanvasElement | null>(null)

  // ── ElevenLabs useConversation (real voice session API) ───────────────────
  // The official @elevenlabs/react hook manages the WebRTC/WebSocket lifecycle
  // directly. It exposes startSession/endSession methods plus rich callbacks.
  // We previously tried mounting <elevenlabs-convai> as a custom element and
  // programmatically clicking its shadow-DOM "Start a Chat" button -- that
  // approach put the widget into text-chat mode and never opened a voice
  // socket. The hook is the documented voice API used by the user's parallel
  // voice work in WfwVoiceWidget.tsx; we mirror that integration here.
  //
  // Callbacks fan out to (a) our recordVoiceEvent diagnostic log so operators
  // can see exactly what the SDK is doing, and (b) UI state (status, transcript).
  // The recordVoiceEvent calls inside these callbacks reference voiceTranscript
  // setters via closure; that is safe because the hook re-binds on each render.
  const conversation = useConversation({
    onConnect: () => {
      // Fired when the WebRTC session opens successfully and audio is flowing.
      recordVoiceEvent('hook:onConnect', {})
      // A start the visitor already abandoned must not reopen the call view.
      if (voiceStartAbort.current?.signal.aborted) return
      callConnectedRef.current = true
      setVoiceMode('active')
      setVoiceStatus('Listening...')

      // Diagnostic: enumerate audio elements + any AudioContext state we can
      // reach on the global so we know whether the SDK's output is going to
      // be audible. Browsers sometimes start AudioContext in 'suspended'
      // state if the connecting code path lost the user-gesture chain --
      // we attempt to resume any context we can find and log the outcome.
      setTimeout(() => {
        try {
          const audios = Array.from(document.querySelectorAll('audio'))
          const audioReport = audios.map((a) => {
            const stream = (a as HTMLAudioElement & { srcObject?: MediaStream | null }).srcObject
            const tracks = stream && stream.getTracks
              ? stream.getTracks().map((t) => ({ kind: t.kind, label: t.label, enabled: t.enabled, muted: t.muted, readyState: t.readyState }))
              : []
            return {
              muted: a.muted,
              paused: a.paused,
              volume: a.volume,
              readyState: a.readyState,
              hasStream: !!stream,
              tracks,
            }
          })
          recordVoiceEvent('audit:audio-elements', { count: audios.length, details: audioReport })

          // Force-play any audio that's paused -- the SDK should auto-play but
          // browsers can suspend if anything in the gesture chain looks off.
          for (const a of audios) {
            if (a.paused) void a.play().catch((err) => recordVoiceEvent('audit:audio-play-error', { message: err instanceof Error ? err.message : String(err) }))
          }
        } catch (err) {
          recordVoiceEvent('audit:audio-probe-error', { message: err instanceof Error ? err.message : String(err) })
        }
      }, 1500)
    },
    onDisconnect: () => {
      // Fired when either side ends the call. We treat this as a signal to
      // tear down our UI state and persist the call summary via /crm.
      recordVoiceEvent('hook:onDisconnect', {})
      // Only auto-transition to idle if we are not currently in error state.
      // The error path keeps the error message visible so the user can retry.
      setVoiceMode((prev) => (prev === 'error' ? 'error' : 'idle'))
      setVoiceStatus('Disconnected')
      // Remote hang-up: record the call. A no-op when a local path already did.
      finishCall('ended')
    },
    onMessage: ({ message, source }: { message: string; source: string }) => {
      // source is 'user' | 'ai' from the SDK; normalise to 'agent'.
      const speaker: 'user' | 'agent' = source === 'ai' ? 'agent' : 'user'
      recordVoiceEvent('hook:onMessage', { source, message: message.slice(0, 200) })
      const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      const turn: VoiceTurn = { id: genId(), speaker, text: message, timestamp }
      voiceTranscriptRef.current = [...voiceTranscriptRef.current, turn]
      setVoiceTranscript((prev) => [...prev, turn])
      setVoiceStatus(speaker === 'user' ? 'Listening...' : 'Catalyst is speaking...')
    },
    onError: (errorMsg: string) => {
      // Always show the exact SDK error per the global "exact errors" rule.
      recordVoiceEvent('hook:onError', { error: errorMsg })
      setVoiceMode('error')
      setVoiceError(errorMsg)
    },
  })

  /** Output-side audio diagnostic state. Mirrors conversation.isSpeaking +
   *  derived peak level from getOutputByteFrequencyData() so we can tell
   *  whether the agent's audio is being received from the WebSocket but
   *  failing to play through speakers. */
  const [agentSpeaking, setAgentSpeaking] = useState(false)
  const [outputLevel, setOutputLevel] = useState(0)

  // Poll the conversation hook's input + output frequency analysers at 30fps
  // so the operator can see audio flowing in both directions. If the input
  // (mic) bar stays flat while you're speaking, the SDK isn't getting your
  // audio. If the output bar stays flat while voiceStatus says "Catalyst is
  // speaking", the WebSocket isn't returning audio frames. If the output bar
  // moves but you can't hear anything, the browser tab is muted or the wrong
  // output device is selected.
  useEffect(() => {
    if (voiceMode !== 'active') {
      // Reset visual indicators when voice mode exits. React batches these
      // three setState calls so they don't cause cascading renders.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAgentSpeaking(false)
      setOutputLevel(0)
      setMicLevel(0)
      return
    }
    let stop = false
    let raf = 0
    const tick = () => {
      if (stop) return
      try {
        // The hook is non-null here because voiceMode==='active' means session is live.
        const isSpeak = conversation.isSpeaking
        if (isSpeak !== agentSpeaking) setAgentSpeaking(isSpeak)
        const outData = conversation.getOutputByteFrequencyData?.()
        if (outData && outData.length > 0) {
          let peak = 0
          for (let i = 0; i < outData.length; i++) if (outData[i] > peak) peak = outData[i]
          setOutputLevel(Math.round((peak / 255) * 100))
        }
        const inData = conversation.getInputByteFrequencyData?.()
        if (inData && inData.length > 0) {
          let peak = 0
          for (let i = 0; i < inData.length; i++) if (inData[i] > peak) peak = inData[i]
          setMicLevel(Math.round((peak / 255) * 100))
        }
      } catch {
        // Hook may not be ready yet; ignore and try again next frame.
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      stop = true
      cancelAnimationFrame(raf)
    }
  }, [voiceMode, conversation, agentSpeaking])

  // ── Waveform rendering for troubleshooting panel ─────────────────────────
  // Draws live waveforms onto two canvas elements (input from mic + output
  // from agent) using the SDK's frequency data buffers. Only runs while the
  // troubleshooting panel is open and the call is active, so we never burn
  // CPU rendering invisible canvases.
  useEffect(() => {
    if (!debugOpen || voiceMode !== 'active') return
    let stop = false
    let raf = 0

    /** Renders one frame onto a canvas using the byte-frequency data array.
     *  Bars start at the bottom, height proportional to bin amplitude.
     *  Color shifts from gray (silent) to accent (loud) so silence is
     *  visually obvious. */
    const drawBars = (
      canvas: HTMLCanvasElement | null,
      data: Uint8Array | undefined,
      accent: string
    ) => {
      if (!canvas || !data || data.length === 0) return
      const ctx2d = canvas.getContext('2d')
      if (!ctx2d) return
      const w = canvas.width
      const h = canvas.height
      ctx2d.clearRect(0, 0, w, h)
      const bins = Math.min(48, data.length)
      const barW = w / bins
      for (let i = 0; i < bins; i++) {
        const v = data[Math.floor((i / bins) * data.length)] / 255
        const barH = v * h
        // Loud bins use brand accent; quiet bins fade to gray for clarity.
        ctx2d.fillStyle = v > 0.3 ? accent : '#d1d5db'
        ctx2d.fillRect(i * barW, h - barH, Math.max(1, barW - 1), barH)
      }
    }

    const tick = () => {
      if (stop) return
      try {
        drawBars(inputWaveformRef.current, conversation.getInputByteFrequencyData?.(), theme.accent)
        drawBars(outputWaveformRef.current, conversation.getOutputByteFrequencyData?.(), theme.accent)
      } catch {
        // Hook not ready / canvas unmounted -- skip frame.
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      stop = true
      cancelAnimationFrame(raf)
    }
  }, [debugOpen, voiceMode, conversation, theme.accent])

  // (runMicRoundtripTest is defined further down, after recordVoiceEvent --
  // it depends on that callback, so moving it later avoids a TDZ error.)

  // ── Refs ─────────────────────────────────────────────────────────────────

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Reference to the hidden container div that holds the <elevenlabs-convai> element.
   * Kept in a ref so we can remove the element (and stop audio) from anywhere
   * without triggering a re-render.
   */
  const convaiContainerRef = useRef<HTMLDivElement | null>(null)
  /**
   * Reference to the mounted <elevenlabs-convai> custom element.
   * Used for event listener teardown and DOM removal on end-call.
   */
  const convaiElRef = useRef<HTMLElement | null>(null)

  // ── Voice diagnostic refs ────────────────────────────────────────────────
  /** AudioContext used to drive the mic-level analyser (created on call start). */
  const audioCtxRef = useRef<AudioContext | null>(null)
  /** AnalyserNode that reads mic frames at audio rate. */
  const analyserRef = useRef<AnalyserNode | null>(null)
  /** MediaStream returned by getUserMedia for our diagnostic mic-tap. */
  const micStreamRef = useRef<MediaStream | null>(null)
  /** RAF handle for the mic-level update loop, so we can cancel it on cleanup. */
  const micRafRef = useRef<number | null>(null)
  /** Stores the original dispatchEvent on the convai element so we can restore it. */
  const origDispatchRef = useRef<typeof EventTarget.prototype.dispatchEvent | null>(null)

  // ── Session persistence (whenever session state changes) ──────────────────

  /**
   * Writes the current session to localStorage whenever any persisted
   * field changes. Skips writes during the initial empty-state render
   * (sessionId is set on the hydration effect above).
   */
  useEffect(() => {
    if (!sessionId) return
    saveSession({
      sessionId,
      messages,
      userTurns,
      capturedEmail,
      activeEscalation,
    })
  }, [sessionId, messages, userTurns, capturedEmail, activeEscalation])

  // ── Auto-scroll ───────────────────────────────────────────────────────────

  /** Scrolls to the bottom of the message area after each new message. */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isStreaming, showEmailCapture])

  // ── Focus input on open ───────────────────────────────────────────────────

  /** Focuses the textarea when the panel opens for keyboard users. */
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  // ── Inactivity timer ──────────────────────────────────────────────────────

  /**
   * Resets the 5-minute inactivity timer.
   * When the timer fires, saves session to CRM (fire-and-forget).
   * Only fires if there's at least one assistant reply and CRM hasn't been saved.
   */
  const resetInactivity = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    inactivityTimer.current = setTimeout(() => {
      if (messages.length > 1 && !crmSaved) {
        void saveToCrm({
          sessionId,
          email: capturedEmail ?? undefined,
          messages: messages.map(({ role, content }) => ({ role, content })),
          escalationTrigger: activeEscalation ?? undefined,
          partnerSlug: theme.partnerSlug,
        })
        setCrmSaved(true)
      }
    }, 5 * 60 * 1000)
  }, [
    messages,
    crmSaved,
    capturedEmail,
    activeEscalation,
    sessionId,
    theme.partnerSlug,
  ])

  useEffect(() => {
    if (isOpen && messages.length > 1) resetInactivity()
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    }
  }, [isOpen, messages.length, resetInactivity])

  const voiceStartAbort = useRef<AbortController | null>(null)
  const voiceLaunchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    voiceStartAbort.current?.abort()
    if (voiceLaunchTimer.current !== null) clearTimeout(voiceLaunchTimer.current)
  }, [])

  function queueVoiceStart() {
    if (voiceLaunchTimer.current !== null) clearTimeout(voiceLaunchTimer.current)
    voiceLaunchTimer.current = setTimeout(() => {
      voiceLaunchTimer.current = null
      void handleCallCatalyst()
    }, 150)
  }

  // ── catalyst:open event listener ─────────────────────────────────────────

  /**
   * Allows other pages to programmatically open the chat widget.
   * Example: window.dispatchEvent(new Event('catalyst:open'))
   * Used by the site search page and any CTA buttons that want to
   * pre-open the widget.
   */
  const openWidget = useCallback(() => {
    setIsOpen(true)
    if (!hasOpened) {
      setHasOpened(true)
      setMessages([{ id: GREETING_ID, role: 'assistant', content: greetingText }])
    }
  }, [hasOpened, greetingText])

  useEffect(() => {
    /**
     * catalyst:open — open the chat panel.
     *
     * Wave 5c: when the admin has set defaultMode='voice' (partner_config) AND
     * voice is enabled, an external CTA dispatching catalyst:open should launch
     * straight into a live call — the same voice-first behavior the collapsed
     * bubble already has (Branch 2 of the docked-pill button). This makes
     * "default to voice" a real, end-to-end setting that ALSO governs external
     * "Talk to <agent>" buttons, not just the widget's own bubble. The 150ms
     * delay mirrors the bubble path: it gives React time to commit isOpen=true
     * so handleCallCatalyst() finds the ConvAI mount point. When defaultMode
     * is 'chat'/absent or the config has not loaded (shouldVoiceDefault=false),
     * behavior is unchanged — the panel opens to chat exactly as before.
     */
    const handler = () => {
      openWidget()
      if (shouldVoiceDefault) {
        queueVoiceStart()
      }
    }
    window.addEventListener('catalyst:open', handler)
    return () => window.removeEventListener('catalyst:open', handler)
    // queueVoiceStart is a render-scoped function declaration; including it keeps
    // the listener bound to the latest closure (and satisfies exhaustive-deps).
  }, [openWidget, shouldVoiceDefault, queueVoiceStart])

  useEffect(() => {
    /**
     * catalyst:open-voice — open the widget and start a live voice call
     * immediately, regardless of the defaultMode setting. This gives host pages
     * an explicit, unambiguous voice launcher (used by the Teamphoria "Talk to
     * Linda Now" button) that does not depend on partner_config timing. It is a
     * no-op for voice unless voice is actually enabled for the agent: if voice
     * is disabled, handleCallCatalyst() surfaces the standard inline error and
     * the panel simply stays in chat mode.
     */
    const handler = () => {
      openWidget()
      queueVoiceStart()
    }
    window.addEventListener('catalyst:open-voice', handler)
    return () => window.removeEventListener('catalyst:open-voice', handler)
    // See note above: include queueVoiceStart so the listener always calls the
    // latest closure and the exhaustive-deps lint rule is satisfied.
  }, [openWidget, queueVoiceStart])

  // ── ConvAI element cleanup ────────────────────────────────────────────────

  /**
   * Removes the <elevenlabs-convai> element from the DOM, stopping all audio
   * streams and WebSocket connections managed by the ElevenLabs SDK.
   * Also cleans up the event listeners attached by handleCallCatalyst().
   * Safe to call even if the element was never mounted.
   * Declared here (before closeWidget) so closeWidget can reference it.
   */
  const teardownConvai = useCallback(() => {
    voiceStartAbort.current?.abort()
    if (voiceLaunchTimer.current !== null) clearTimeout(voiceLaunchTimer.current)
    // Restore native dispatchEvent first so the disconnect call below doesn't
    // re-enter our wildcard logger after we've already started teardown.
    if (convaiElRef.current && origDispatchRef.current) {
      try {
        ;(convaiElRef.current as unknown as { dispatchEvent: typeof EventTarget.prototype.dispatchEvent }).dispatchEvent =
          origDispatchRef.current
      } catch {
        // ignore
      }
      origDispatchRef.current = null
    }
    if (convaiElRef.current) {
      try {
        // Attempt to call disconnect if the element exposes it
        if (typeof (convaiElRef.current as unknown as { disconnect?: () => void }).disconnect === 'function') {
          ;(convaiElRef.current as unknown as { disconnect: () => void }).disconnect()
        }
      } catch {
        // Best-effort -- the element may already be in a bad state
      }
      convaiElRef.current.remove()
      convaiElRef.current = null
    }
    // Tear down the mic-level analyser pipeline.
    if (micRafRef.current !== null) {
      cancelAnimationFrame(micRafRef.current)
      micRafRef.current = null
    }
    if (micStreamRef.current) {
      for (const track of micStreamRef.current.getTracks()) track.stop()
      micStreamRef.current = null
    }
    if (audioCtxRef.current) {
      try { void audioCtxRef.current.close() } catch { /* ignore */ }
      audioCtxRef.current = null
    }
    analyserRef.current = null
    setMicLevel(0)
    setMicState('idle')
  }, [])

  useEffect(() => {
    /**
     * Terminate any active voice call when the user closes or hides the browser tab.
     * Prevents dangling ElevenLabs sessions that consume quota after the user navigates
     * away. visibilitychange fires on both hide and show, so guard on visibilityState.
     * Placed after teardownConvai's useCallback declaration so tsc does not flag
     * the reference as a use-before-assign.
     */
    // Policy (#1888): hiding the tab still ends the call to protect quota, and
    // the thread gets a visible "Call ended because this tab was hidden." row.
    const terminateOnHide = () => {
      if (voiceMode === 'active' || voiceMode === 'requesting') {
        try { conversation.endSession() } catch { /* ignore */ }
        teardownConvai()
        setVoiceMode('idle')
        finishCall('hidden')
      }
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') terminateOnHide()
    }
    window.addEventListener('pagehide', terminateOnHide)
    window.addEventListener('beforeunload', terminateOnHide)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', terminateOnHide)
      window.removeEventListener('beforeunload', terminateOnHide)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [voiceMode, conversation, teardownConvai, finishCall])

  /** Moves focus to the composer after a return to text, once it has mounted. */
  useEffect(() => {
    if (voiceMode === 'idle' && focusComposerRef.current) {
      focusComposerRef.current = false
      inputRef.current?.focus()
    }
  }, [voiceMode])

  // ── Voice diagnostic helpers ──────────────────────────────────────────────

  /**
   * Append one event entry to the in-memory voice event log (capped at 30
   * to keep render cost cheap). Also console.logs with a clear prefix and
   * fires-and-forgets a copy to the server-side debug endpoint so we can
   * audit a full call from the dev server log.
   */
  const recordVoiceEvent = useCallback(
    (type: string, detail: unknown) => {
      const entry = {
        id: genId(),
        type,
        detail: (() => {
          try {
            return typeof detail === 'string' ? detail : JSON.stringify(detail)
          } catch {
            return String(detail)
          }
        })().slice(0, 400),
        ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      }
      console.log('[Catalyst:Voice]', entry.ts, type, detail)
      setVoiceEvents((prev) => {
        const next = [...prev, entry]
        return next.length > 30 ? next.slice(-30) : next
      })
      // Fire-and-forget server log; we ignore failures since it's diagnostics.
      try {
        void fetch(`${catalystApiBase}/api/catalyst/voice/debug`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, type, detail: entry.detail, ts: Date.now() }),
          keepalive: true,
        }).catch(() => undefined)
      } catch {
        // Browser may reject non-keepalive on some paths; ignore.
      }
    },
    [sessionId]
  )

  /**
   * Records the user's mic for ~3 seconds, then plays it back so the user
   * can verify both that capture works AND that playback works (a full
   * roundtrip test). On any failure we log the exact error to the diagnostic
   * stream. Used by the "Test microphone" button in the troubleshooting panel.
   */
  const runMicRoundtripTest = useCallback(async () => {
    if (micTestState !== 'idle') return
    recordVoiceEvent('audit:mic-test-start', {})
    setMicTestState('recording')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Pick a MIME the browser supports. webm/opus is standard in Chrome/Firefox.
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'
      const recorder = new MediaRecorder(stream, { mimeType: mime })
      const chunks: BlobPart[] = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }
      recorder.onstop = async () => {
        // Stop the mic right away so the SDK can claim it back if needed.
        for (const t of stream.getTracks()) t.stop()
        const blob = new Blob(chunks, { type: mime })
        recordVoiceEvent('audit:mic-test-recorded', { sizeBytes: blob.size, mime })
        setMicTestState('playing')
        const url = URL.createObjectURL(blob)
        const a = new Audio(url)
        a.onended = () => {
          URL.revokeObjectURL(url)
          setMicTestState('idle')
          recordVoiceEvent('audit:mic-test-played', {})
        }
        a.onerror = () => {
          URL.revokeObjectURL(url)
          setMicTestState('idle')
          recordVoiceEvent('audit:mic-test-play-error', { err: a.error?.code ?? 'unknown' })
        }
        try {
          await a.play()
        } catch (err) {
          setMicTestState('idle')
          recordVoiceEvent('audit:mic-test-play-throw', {
            message: err instanceof Error ? err.message : String(err),
          })
        }
      }
      recorder.start()
      // Record exactly 3 seconds. Long enough for the user to say "testing
      // 1 2 3" and short enough to feel snappy.
      setTimeout(() => recorder.stop(), 3000)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      const denied = err instanceof DOMException && err.name === 'NotAllowedError'
      recordVoiceEvent('audit:mic-test-error', { message: msg, denied })
      setMicTestState('idle')
    }
  }, [micTestState, recordVoiceEvent])

  /**
   * Opens an AudioContext + AnalyserNode on the user's mic so we can show a
   * real-time input-level bar. This proves the mic is being captured. We
   * request our own getUserMedia stream alongside the convai element's; modern
   * browsers grant both consumers the same hardware stream without re-prompting
   * for permission. If permission is denied we surface the exact error.
   */
  const startMicAnalyser = useCallback(async () => {
    setMicState('requesting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      micStreamRef.current = stream
      setMicState('granted')
      recordVoiceEvent('mic:granted', {
        tracks: stream.getAudioTracks().map((t) => ({ label: t.label, enabled: t.enabled, muted: t.muted })),
      })

      // AudioContext typed as any because TS lib doesn't know about webkitAudioContext fallback.
      const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)
      const audioCtx = new Ctx()
      audioCtxRef.current = audioCtx
      const source = audioCtx.createMediaStreamSource(stream)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 512
      analyser.smoothingTimeConstant = 0.5
      source.connect(analyser)
      analyserRef.current = analyser

      const buffer = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        if (!analyserRef.current) return
        analyserRef.current.getByteTimeDomainData(buffer)
        // Compute peak deviation from the 128 zero-line, scale 0-100.
        let peak = 0
        for (let i = 0; i < buffer.length; i++) {
          const dev = Math.abs(buffer[i] - 128)
          if (dev > peak) peak = dev
        }
        setMicLevel(Math.min(100, Math.round((peak / 128) * 100)))
        micRafRef.current = requestAnimationFrame(tick)
      }
      micRafRef.current = requestAnimationFrame(tick)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      // DOMException 'NotAllowedError' is the standard for user-denied permissions.
      const denied = err instanceof DOMException && err.name === 'NotAllowedError'
      setMicState(denied ? 'denied' : 'error')
      recordVoiceEvent('mic:error', { message: msg, denied })
      // Re-throw so handleCallCatalyst can surface the error to the user.
      throw err
    }
  }, [recordVoiceEvent])

  // ── Close handler ─────────────────────────────────────────────────────────

  /**
   * Closes the widget panel and triggers the CRM save if this is the
   * first close and the conversation has at least one assistant reply.
   * Uses keepalive fetch so the request survives even if the page navigates.
   * Also tears down any active voice session -- closing the panel ends the call.
   */
  const closeWidget = useCallback(() => {
    if (voiceLaunchTimer.current !== null) clearTimeout(voiceLaunchTimer.current)
    setIsOpen(false)
    // End any active voice session when the panel closes.
    // conversation.endSession() tells ElevenLabs to tear down the WebRTC stream;
    // teardownConvai() then cleans up local mic/audio resources.
    // Both must run so the audio stream doesn't keep going after the UI collapses.
    if (voiceMode === 'active' || voiceMode === 'requesting') {
      try { conversation.endSession() } catch {}
      teardownConvai()
      setVoiceMode('idle')
      finishCall('ended')
    }
    if (messages.length > 1 && !crmSaved) {
      void saveToCrm({
        sessionId,
        email: capturedEmail ?? undefined,
        messages: messages.map(({ role, content }) => ({ role, content })),
        escalationTrigger: activeEscalation ?? undefined,
        partnerSlug: theme.partnerSlug,
      })
      setCrmSaved(true)
    }
  }, [
    messages,
    crmSaved,
    capturedEmail,
    activeEscalation,
    sessionId,
    theme.partnerSlug,
    voiceMode,
    teardownConvai,
    finishCall,
    conversation,   // needed so the hook captures the current conversation reference
  ])

  // ── Voice config check (on mount) ────────────────────────────────────────

  /**
   * Fires once on widget mount to check whether this agent has web_widget_enabled=true.
   * The agentId comes from brand.catalyst.agentId (baked into the brand config at build time).
   * On success: populates voiceEnabled + voiceConfig so the "Call" button shows/hides correctly.
   * On network error or 404: sets voiceEnabled=false (graceful degrade -- button stays hidden).
   */
  useEffect(() => {
    const agentId = brand.catalyst.agentId
    if (!agentId) {
      // No agent configured for this brand -- hide voice button. Single
      // setState, batched into the initial commit.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVoiceEnabled(false)
      return
    }

    let cancelled = false

    async function checkVoiceConfig() {
      try {
        const res = await fetch(`${catalystApiBase}/api/voice-widget/config/${agentId}`)
        if (cancelled) return

        if (!res.ok) {
          // 404 or server error -- treat as disabled
          console.warn('[Catalyst] Voice config check failed:', res.status)
          setVoiceEnabled(false)
          return
        }

        const data = (await res.json()) as VoiceConfig

        if (cancelled) return

        if (data.enabled) {
          setVoiceEnabled(true)
          setVoiceConfig(data)
          // Wave 5b: store the defaultMode from the config so the bubble can
          // decide whether to launch voice immediately or open the chat panel.
          // Default to 'chat' when the field is absent (backward compatibility).
          setVoiceDefaultMode(data.defaultMode ?? 'chat')
        } else {
          // enabled=false: voice is off for this agent. Do NOT set voiceDefaultMode
          // to 'voice' even if somehow the payload included it -- we treat the whole
          // config as disabled and fall through to chat behavior.
          setVoiceEnabled(false)
        }
      } catch (err) {
        if (cancelled) return
        // Network error -- degrade gracefully, never crash
        console.warn('[Catalyst] Voice config network error:', err)
        setVoiceEnabled(false)
      }
    }

    void checkVoiceConfig()

    return () => {
      cancelled = true
    }
    // Only run once on mount -- agentId is a compile-time constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── ConvAI script loader (idempotent) ─────────────────────────────────────

  /**
   * Loads the ElevenLabs ConvAI embed script once, only when voice mode becomes
   * active and the script is not yet present.
   * Uses customElements.get() to detect whether the element is already registered
   * (avoids double-loading if the script was already injected by another surface).
   *
   * The script registers the <elevenlabs-convai> custom element globally.
   * Once loaded, subsequent calls to mountConvai() can use the element immediately.
   */
  function ensureConvaiScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Already registered -- nothing to load
      if (typeof customElements !== 'undefined' && customElements.get('elevenlabs-convai')) {
        resolve()
        return
      }

      // Script tag already injected but not yet registered -- wait for load
      const existing = document.querySelector(
        'script[src*="elevenlabs"][src*="convai"]'
      ) as HTMLScriptElement | null

      if (existing) {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', (e) => reject(new Error(`ConvAI script load error: ${String(e)}`)))
        return
      }

      // Inject fresh script tag
      const script = document.createElement('script')
      script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed'
      script.async = true
      script.onload = () => resolve()
      script.onerror = (e) =>
        reject(new Error(`Failed to load ElevenLabs ConvAI script: ${String(e)}`))
      document.head.appendChild(script)
    })
  }

  // ── Voice mode -- "Call Catalyst" click handler ───────────────────────────

  /**
   * Initiates the voice flow when the user clicks "Call Catalyst".
   *
   * Flow:
   *   1. Guard: if another session is already active, do nothing (no double-mount).
   *   2. Set voiceMode='requesting' -- shows spinner + "Connecting..." text.
   *   3. Fetch a signed WebSocket URL from /api/voice-widget/signed-url.
   *   4. On success: load the ConvAI script, mount the element, set voiceMode='active'.
   *   5. On failure: set voiceMode='error' with the exact error string.
   */
  async function handleCallCatalyst() {
    // Guard: already active -- ignore duplicate clicks
    if (voiceStartAbort.current || voiceMode === 'active' || voiceMode === 'requesting') return

    const agentId = brand.catalyst.agentId
    if (!agentId) {
      setVoiceMode('error')
      setVoiceError('No agent ID configured for this brand.')
      return
    }

    const attempt = new AbortController()
    voiceStartAbort.current = attempt
    callIdRef.current = genId()
    callConnectedRef.current = false
    voiceTranscriptRef.current = []
    setCallEndedNotice('')
    setVoiceMode('requesting')
    setVoiceStatus('Connecting...')
    setVoiceError('')
    setVoiceTranscript([])
    setIsMuted(false)

    try {
      // Reset diagnostics state at the start of every call so the panel shows
      // fresh data instead of stale events from a prior session.
      setVoiceEvents([])
      setMicLevel(0)

      // Pre-warm the mic permission. WfwVoiceWidget calls getUserMedia BEFORE
      // startSession to (a) surface a clean permission denial up front, (b)
      // chain the user-activation gesture into the audio system so the SDK's
      // AudioContext is allowed to play output. We do the same here, but
      // IMMEDIATELY stop the tracks once the permission is granted -- holding
      // the stream conflicts with the SDK's own mic capture later.
      recordVoiceEvent('flow:start', { agentId, sessionId })
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('This browser does not support voice calls. You can keep chatting in text.')
        }
        const probeStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        const labels = probeStream.getAudioTracks().map((t) => t.label)
        // Release immediately so the SDK can claim the mic itself.
        for (const t of probeStream.getTracks()) t.stop()
        if (attempt.signal.aborted) return
        setMicState('granted')
        recordVoiceEvent('mic:granted', { tracks: labels })
      } catch (micErr) {
        if (attempt.signal.aborted) return
        const msg = micErr instanceof Error ? micErr.message : String(micErr)
        const denied = micErr instanceof DOMException && micErr.name === 'NotAllowedError'
        recordVoiceEvent('mic:error', { message: msg, denied })
        setMicState(denied ? 'denied' : 'error')
        setVoiceMode('error')
        setVoiceError(denied ? 'Microphone access required to talk with Catalyst.' : msg)
        return
      }

      // Step 1: fetch a short-lived signed WebSocket URL.
      // The signed-url route requires the Origin header. Browsers omit Origin
      // on same-origin GETs unless the request is "non-simple", so we add a
      // custom X-Requested-With header which forces the browser to include
      // Origin even when widget and API share the same host (wfw.local).
      // For cross-origin embeds (acme.com -> workforcewave.com/api), Origin
      // is sent automatically and the custom header is harmless.
      recordVoiceEvent('flow:fetch-signed-url', { agentId })
      const urlRes = await fetch(
        `${catalystApiBase}/api/voice-widget/signed-url?agent_id=${encodeURIComponent(agentId)}`,
        { headers: { 'X-Requested-With': 'Catalyst-Widget' }, signal: attempt.signal }
      )

      if (attempt.signal.aborted) return

      if (!urlRes.ok) {
        // Parse the exact error message from the JSON body
        let errMsg = `HTTP ${urlRes.status}`
        try {
          const errBody = (await urlRes.json()) as { error?: string }
          if (errBody.error) errMsg = errBody.error
        } catch {
          // Body wasn't JSON -- use statusText
          errMsg = urlRes.statusText || errMsg
        }

        if (attempt.signal.aborted) return

        // Handle Retry-After for 429
        if (urlRes.status === 429) {
          const retryAfter = urlRes.headers.get('Retry-After')
          errMsg = retryAfter
            ? `Too many requests, try again in ${retryAfter}s`
            : 'Too many requests, please wait and try again.'
        }

        setVoiceMode('error')
        setVoiceError(errMsg)
        return
      }

      const urlData = (await urlRes.json()) as { signedUrl?: string; error?: string }

      if (attempt.signal.aborted) return

      if (!urlData.signedUrl) {
        setVoiceMode('error')
        setVoiceError(urlData.error ?? 'No signed URL returned from server.')
        return
      }

      const signedUrl = urlData.signedUrl

      // Step 2: open the actual voice session via @elevenlabs/react.
      // The hook handles WebRTC peer connection, audio streaming, and event
      // dispatch into our onConnect/onMessage/onError callbacks defined at
      // the top of the component. The signedUrl is the ephemeral conversation
      // signature ElevenLabs accepts in lieu of an API key.
      recordVoiceEvent('flow:start-session', { signedUrl: signedUrl.slice(0, 60) })
      setVoiceMode('active')
      setVoiceStatus('Connecting...')

      // Note: we do NOT pass `overrides.agent.firstMessage` here. The
      // ElevenLabs server rejects override fields that the agent's
      // platform_settings.overrides allowlist does not include, and that
      // rejection arrives as an immediate close-after-connect (visible as
      // hook:onConnect followed by hook:onDisconnect in the same second).
      // To make Catalyst greet first, set web_widget_first_message in the
      // elevenlabs_agents table or update the agent's first_message in the
      // ElevenLabs dashboard. Until then, the user must speak first.
      await conversation.startSession({
        signedUrl,
        dynamicVariables: {
          page_url: typeof window !== 'undefined' ? window.location.href : '',
          page_title: typeof document !== 'undefined' ? document.title : '',
          _session_id: sessionId,
          _source: 'catalyst-chat-widget',
          // Pass selected language so ElevenLabs agent can respond in the right language.
          language: selectedLanguage,
          // Pass recent chat history so the ElevenLabs agent has context of what was
          // discussed before the user switched to voice. Last 6 turns only to stay
          // within ElevenLabs dynamic variable size limits. Call-status rows and
          // the greeting never go to the model (#1888).
          _chat_context: buildModelHistory(messages)
            .slice(-6)
            .map((m) => `[${m.role}] ${m.content}`)
            .join('\n'),
        },
        // CRITICAL: override the SDK's default libsamplerate CDN URL with a
        // locally-hosted copy. The SDK ships with the URL hard-coded to
        // cdn.jsdelivr.net, but loading AudioWorklet modules from that CDN
        // fails on the wfw.local self-signed-cert origin with
        // ERR_SSL_PROTOCOL_ERROR. Without libsamplerate the SDK cannot
        // resample agent audio (PCM 16kHz) to the AudioContext rate (48kHz),
        // so the output stream stays silent even though the audio element
        // shows paused:false. The file is bundled at public/libsamplerate.worklet.js.
        libsampleratePath: `${catalystApiBase}/libsamplerate.worklet.js`,
      })
      if (attempt.signal.aborted) {
        await conversation.endSession()
        return
      }
      recordVoiceEvent('flow:session-started', {})
    } catch (err) {
      if (attempt.signal.aborted) return
      const msg = err instanceof Error ? err.message : String(err)
      console.error('[Catalyst] Voice mode error:', err)
      setVoiceMode('error')
      setVoiceError(msg)
    } finally {
      if (voiceStartAbort.current === attempt) voiceStartAbort.current = null
    }
  }

  // ── End-call handler ──────────────────────────────────────────────────────

  /**
   * Terminates the active voice session.
   * - Removes the <elevenlabs-convai> element (stops audio streams)
   * - Resets voiceMode to 'idle' (chat panel body re-appears)
   * - Appends a system "Voice call ended" message to the chat thread
   * - Fires POST /api/catalyst/crm with conversationSummary='Voice call'
   *   so the call is captured in the CRM if a lead email is known.
   */
  function handleEndCall() {
    recordVoiceEvent('flow:end-call', {})
    // End the @elevenlabs/react session if active. endSession() is a sync
    // call that triggers teardown asynchronously inside the hook; we don't
    // await it. teardownConvai() below cleans up our diagnostic mic-analyser
    // resources independently.
    try {
      conversation.endSession()
    } catch (err) {
      recordVoiceEvent('flow:end-call-error', {
        message: err instanceof Error ? err.message : String(err),
      })
    }
    teardownConvai()
    setVoiceMode('idle')
    setVoiceStatus('Connecting...')
    setIsMuted(false)

    // Build a human-readable call summary from the transcript turns
    const callSummary =
      voiceTranscript.length > 0
        ? `Voice call: ${voiceTranscript
            .map((t) => `[${t.speaker}] ${t.text}`)
            .join(' | ')}`
        : 'Voice call'

    // Spoken turns and one status row go into the persisted thread (#1888).
    finishCall('ended')

    // UX choice (Wave 5b): when the user initiated the call via the voice-default bubble,
    // we collapse the panel back to the docked bubble after the call ends. The user clicked
    // a "Talk" button expecting a voice interaction -- staying in the chat panel would be
    // confusing because they likely do not want to pivot to text. The "Voice call ended"
    // message is still persisted to localStorage and will be visible if they re-open.
    // When voice-default is NOT active (they clicked "Call" from inside the chat panel),
    // we keep the panel open so they can continue the conversation in text -- that is the
    // expected continuation flow for the embedded chat-first surface.
    if (shouldVoiceDefault) {
      setIsOpen(false)
    } else {
      focusComposerRef.current = true
    }

    // Fire-and-forget CRM record -- captures the call even if no chat messages exist
    void saveToCrm({
      sessionId,
      email: capturedEmail ?? undefined,
      messages: [
        ...messages.map(({ role, content }) => ({ role, content })),
        { role: 'assistant', content: callSummary },
      ],
      escalationTrigger: activeEscalation ?? undefined,
      partnerSlug: theme.partnerSlug,
      conversationSummary: callSummary,
    })
  }

  // ── Mute toggle ───────────────────────────────────────────────────────────

  /**
   * Toggles the microphone mute state.
   * Attempts to mute the audio input track by digging into the convai element's
   * shadow DOM to find any active MediaStreamTrack and toggling its enabled flag.
   * If the shadow DOM approach fails (element not yet ready), falls back to a
   * visual-only toggle so the UI stays responsive.
   */
  function handleToggleMute() {
    const nextMuted = !isMuted
    setIsMuted(nextMuted)
    recordVoiceEvent('flow:mute-toggle', { muted: nextMuted })

    // Use the SDK's setMuted method which routes mute through the right
    // audio path. Older code walked all <audio> elements and disabled their
    // tracks, which inadvertently muted the agent's OUTPUT audio element
    // (it has the same kind:'audio' track shape as input would). The
    // @elevenlabs/react useConversation hook exposes setMuted that only
    // mutes the mic input, not the output.
    try {
      conversation.setMuted?.(nextMuted)
    } catch (err) {
      console.warn('[Catalyst] conversation.setMuted error:', err)
    }
  }

  // ── Back-to-chat handler ──────────────────────────────────────────────────

  /**
   * Returns to the chat panel from the voice UI.
   * If a call is active or requesting, it is terminated immediately — voice must
   * not run silently in the background while the user is in the chat view.
   * Unlike handleEndCall(), we do NOT collapse the widget (shouldVoiceDefault path)
   * because the user is explicitly choosing to continue in chat.
   */
  function handleBackToChat() {
    focusComposerRef.current = true
    if (voiceMode === 'active' || voiceMode === 'requesting') {
      // Terminate the ElevenLabs session cleanly. During 'requesting' this also
      // aborts the pending start, so no session or reconnect follows.
      try { conversation.endSession() } catch { /* ignore */ }
      teardownConvai()
      setVoiceMode('idle')
      finishCall('ended')
    } else {
      // No active call — just flip back to the chat view
      setVoiceMode('idle')
    }
  }

  // Clean up convai element when the component unmounts
  useEffect(() => {
    return () => {
      teardownConvai()
    }
  }, [teardownConvai])

  // ── Send message ──────────────────────────────────────────────────────────

  /**
   * Sends the current input as a user message and processes the SSE stream
   * from /api/catalyst/chat. The assistant message is built incrementally
   * from delta chunks and finalized on the "done" event.
   *
   * Error handling:
   * - Network/fetch errors: rendered as an error bubble with exact err.message
   * - API error events:     rendered as an error bubble with exact error string
   * - Both log to console with [Catalyst] prefix
   */
  const sendMessage = useCallback(async () => {
    const text = input.trim()
    if (!text || isStreaming || !sessionId) return

    setInput('')
    resetInactivity()

    const newTurns = userTurns + 1
    setUserTurns(newTurns)

    const userMessage: Message = { id: genId(), role: 'user', content: text }
    setMessages((prev) => [...prev, userMessage])
    setIsStreaming(true)

    // Conversation history for the API: last 6 real turns, typed or spoken.
    const historyMessages = buildModelHistory(messages).slice(-6)

    // ID for the assistant message we are building incrementally
    const assistantMsgId = genId()

    // Add an empty assistant message placeholder so the UI shows the typing indicator area
    setMessages((prev) => [
      ...prev,
      { id: assistantMsgId, role: 'assistant', content: '' },
    ])

    try {
      const res = await fetch(`${catalystApiBase}/api/catalyst/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          message: text,
          email: capturedEmail ?? undefined,
          partnerSlug: brand.slug,
          conversationHistory: historyMessages,
          // Pass selected language so the LLM responds in the visitor's preferred language.
          language: selectedLanguage,
        }),
      })

      if (!res.ok || !res.body) {
        // Non-streaming error (e.g. 400 validation from Zod)
        const errorText = `HTTP ${res.status}: ${res.statusText}`
        console.error('[Catalyst] Chat request failed:', errorText)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? {
                  ...m,
                  content: `Request failed: ${errorText}. Please try again.`,
                  isError: true,
                }
              : m
          )
        )
        setIsStreaming(false)
        return
      }

      // Process SSE stream line-by-line
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let accumulatedContent = ''
      let isEscalationFlag = false
      let finalSources: Source[] = []

      let buffer = ''

      // Stream loop: read chunks and parse SSE events
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        // Keep incomplete line in buffer
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue

          const jsonStr = line.slice(6).trim()
          if (!jsonStr) continue

          let parsed: Record<string, unknown>
          try {
            parsed = JSON.parse(jsonStr) as Record<string, unknown>
          } catch {
            // Malformed SSE line, skip
            continue
          }

          if (parsed.error && typeof parsed.error === 'string') {
            // API reported an error event with an exact message
            console.error('[Catalyst] API error event:', parsed.error)
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? {
                      ...m,
                      content: `${parsed.error as string} — please try again.`,
                      isError: true,
                    }
                  : m
              )
            )
            setIsStreaming(false)
            return
          }

          if (typeof parsed.delta === 'string') {
            // Token chunk event: append to the accumulator and update the live message
            accumulatedContent += parsed.delta
            const snapshot = accumulatedContent
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, content: snapshot }
                  : m
              )
            )
          }

          if (parsed.done === true) {
            // Final event: attach sources + escalation flag to the assistant message
            isEscalationFlag = parsed.isEscalation === true
            finalSources = Array.isArray(parsed.sources)
              ? (parsed.sources as Source[])
              : []

            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? {
                      ...m,
                      content: accumulatedContent,
                      sources: finalSources,
                      isEscalation: isEscalationFlag,
                    }
                  : m
              )
            )

            // Carry escalation trigger forward for CRM and EscalationCard
            if (isEscalationFlag && !activeEscalation) {
              setActiveEscalation('pricing') // default label; API set it based on content
            }

            // Trigger inline email capture after 2 user turns if no email captured yet
            if (newTurns === 2 && !capturedEmail && !showEmailCapture) {
              setTimeout(() => setShowEmailCapture(true), 500)
            }
          }
        }
      }
    } catch (err) {
      // Network or stream read error — show exact error message
      const errorMsg = err instanceof Error ? err.message : String(err)
      console.error('[Catalyst] Fetch error:', err)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                content: `Connection error: ${errorMsg}. Please try again.`,
                isError: true,
              }
            : m
        )
      )
    } finally {
      setIsStreaming(false)
    }
  }, [
    input,
    isStreaming,
    messages,
    userTurns,
    capturedEmail,
    showEmailCapture,
    activeEscalation,
    resetInactivity,
    sessionId,
    brand.slug,
    selectedLanguage,
  ])

  /** Enter sends, Shift+Enter inserts a newline. */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        void sendMessage()
      }
    },
    [sendMessage]
  )

  // ── Email capture handler ─────────────────────────────────────────────────

  /**
   * Called when the user submits the inline email capture form.
   * Hides the form, adds a confirmation message, and fires the CRM route.
   */
  const handleEmailSubmit = (email: string) => {
    setCapturedEmail(email)
    setShowEmailCapture(false)
    setMessages((prev) => [
      ...prev,
      {
        id: genId(),
        role: 'assistant',
        content: `Got it. I've noted your email (${email}). Our team will reach out with relevant resources. Is there anything else I can help with?`,
      },
    ])
    void saveToCrm({
      sessionId,
      email,
      messages: messages.map(({ role, content }) => ({ role, content })),
      escalationTrigger: activeEscalation ?? undefined,
      partnerSlug: theme.partnerSlug,
    })
    setCrmSaved(false) // allow a subsequent close to re-save with updated state
  }

  // ── Retry handler ─────────────────────────────────────────────────────────

  /**
   * Removes the last error message and re-submits it.
   * Attached to the "Retry" button on error bubbles.
   */
  const handleRetry = useCallback(
    (failedContent: string) => {
      // Remove the error bubble
      setMessages((prev) => prev.filter((m) => !m.isError))
      // Re-prime the input and send
      setInput(failedContent)
      // Use a microtask delay so state settles before sendMessage reads input
      setTimeout(() => {
        setInput('')
        const fakeText = failedContent
        if (!fakeText || isStreaming || !sessionId) return

        resetInactivity()
        const newTurns = userTurns + 1
        setUserTurns(newTurns)

        const userMessage: Message = {
          id: genId(),
          role: 'user',
          content: fakeText,
        }
        setMessages((prev) => [...prev, userMessage])
        setIsStreaming(true)

        const historyMessages = buildModelHistory(messages).slice(-6)

        const assistantMsgId = genId()
        setMessages((prev) => [
          ...prev,
          { id: assistantMsgId, role: 'assistant', content: '' },
        ])

        // Inline async — same SSE logic as sendMessage but scoped here
        ;(async () => {
          try {
            const res = await fetch(`${catalystApiBase}/api/catalyst/chat`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId,
                message: fakeText,
                email: capturedEmail ?? undefined,
                partnerSlug: brand.slug,
                conversationHistory: historyMessages,
              }),
            })

            if (!res.ok || !res.body) {
              const errorText = `HTTP ${res.status}: ${res.statusText}`
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        content: `Request failed: ${errorText}. Please try again.`,
                        isError: true,
                      }
                    : m
                )
              )
              return
            }

            const reader = res.body.getReader()
            const decoder = new TextDecoder()
            let accumulatedContent = ''
            let buffer = ''

            while (true) {
              const { done, value } = await reader.read()
              if (done) break
              buffer += decoder.decode(value, { stream: true })
              const lines = buffer.split('\n')
              buffer = lines.pop() ?? ''

              for (const line of lines) {
                if (!line.startsWith('data: ')) continue
                const jsonStr = line.slice(6).trim()
                if (!jsonStr) continue
                let parsed: Record<string, unknown>
                try {
                  parsed = JSON.parse(jsonStr) as Record<string, unknown>
                } catch {
                  continue
                }
                if (parsed.error && typeof parsed.error === 'string') {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMsgId
                        ? {
                            ...m,
                            content: `${parsed.error as string} — please try again.`,
                            isError: true,
                          }
                        : m
                    )
                  )
                  return
                }
                if (typeof parsed.delta === 'string') {
                  accumulatedContent += parsed.delta
                  const snapshot = accumulatedContent
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMsgId
                        ? { ...m, content: snapshot }
                        : m
                    )
                  )
                }
                if (parsed.done === true) {
                  const sources = Array.isArray(parsed.sources)
                    ? (parsed.sources as Source[])
                    : []
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMsgId
                        ? {
                            ...m,
                            content: accumulatedContent,
                            sources,
                            isEscalation: parsed.isEscalation === true,
                          }
                        : m
                    )
                  )
                }
              }
            }
          } catch (err) {
            const errorMsg = err instanceof Error ? err.message : String(err)
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? {
                      ...m,
                      content: `Connection error: ${errorMsg}. Please try again.`,
                      isError: true,
                    }
                  : m
              )
            )
          } finally {
            setIsStreaming(false)
          }
        })()
      }, 0)
    },
    [
      messages,
      isStreaming,
      sessionId,
      userTurns,
      capturedEmail,
      resetInactivity,
      brand.slug,
    ]
  )

  // ── Render ────────────────────────────────────────────────────────────────

  // Skip render until after client mount so SSR HTML and first-pass client
  // render match (both produce nothing). Without this guard, the lazy
  // useState initializers above would call genId() and read localStorage
  // during hydration and the resulting state would not match the server
  // output, causing React to bail on hydrating the subtree.
  if (!mounted) return null

  /** Text for the polite status region: call progress, voice errors, call end. */
  const liveAnnouncement =
    voiceMode === 'requesting'
      ? 'Connecting'
      : voiceMode === 'active'
        ? voiceStatus === 'Connecting...' ? 'Connecting' : 'Call started'
        : voiceMode === 'error'
          ? `Voice error: ${voiceError}`
          : callEndedNotice

  return (
    <div
      // Desktop: floating bottom-right. Mobile (max-sm, <=639px): span the full
      // viewport width with small margins so the expanded panel can go nearly
      // full-width instead of a cramped 340px box (items-end keeps the collapsed
      // bubble in the corner). Fixes the clipped/cramped widget on phones.
      className="fixed bottom-6 right-6 z-50 flex flex-col items-end max-sm:left-3 max-sm:right-3 max-sm:bottom-3"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {/* ── Expanded panel ────────────────────────────────────────────────── */}
      {isOpen && (
        <div
          // Width: 340px on desktop; on mobile (max-sm) go nearly full-width
          // (viewport minus the 12px container margins on each side) so the chat
          // panel is usable and text isn't clipped. Height grows up to 680 but
          // caps at viewport minus top/bottom padding (24px bottom margin + 80px
          // reserved at top for the floating close button + breathing room) so it
          // never overflows the viewport on shorter screens.
          className="flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden mb-3 w-[340px] max-sm:w-[calc(100vw-24px)]"
          style={{ height: 'min(680px, calc(100vh - 104px))' }}
          role="dialog"
          aria-label={`${agentName} AI Assistant`}
          aria-modal="false"
        >
          {/* ── Header ───────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100 flex-shrink-0">
            {/* Left: avatar + name + subtitle */}
            <div className="flex items-center gap-2.5 min-w-0">
              {/* Square gradient avatar tile with sparkle icon */}
              <div
                className="flex-shrink-0 flex items-center justify-center rounded-[10px]"
                style={{
                  width: 40,
                  height: 40,
                  background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`,
                }}
              >
                <SparkleIcon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 leading-tight">
                  {agentName}
                </p>
                <p className="text-[10px] text-gray-400 leading-tight truncate">
                  AI assistant &middot; {brand.name}
                </p>
              </div>
            </div>

            {/* Right: language selector (when multi-lang) + Call button + close */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/*
               * Language selector: shown only when the agent supports more than one language.
               * Changing the selection updates selectedLanguage state which is passed to
               * both the chat POST body and the voice session dynamicVariables.
               */}
              {languages.length > 1 && (
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 focus:outline-none"
                  aria-label="Select language"
                  title="Select conversation language"
                >
                  {languages.map((code) => (
                    <option key={code} value={code}>
                      {isValidLanguageCode(code) ? LANGUAGE_DISPLAY_NAMES[code] : code.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}

              {/*
               * voiceEnabled=null means the config check is still in flight -- hide button.
               * voiceEnabled=false means the agent has web_widget_enabled=false -- hide button.
               * voiceEnabled=true means the agent is web-enabled -- show the button.
               * voiceModeEnabled=false means 'voice' was excluded from modes -- always hide.
               * While voiceMode='requesting', show a disabled spinner state instead.
               */}
              {voiceModeEnabled && voiceEnabled === true && voiceMode !== 'active' && (
                <button
                  type="button"
                  onClick={() => void handleCallCatalyst()}
                  disabled={voiceMode === 'requesting'}
                  className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: theme.accent }}
                  title={voiceConfig?.buttonText ?? 'Call Catalyst (voice mode)'}
                >
                  {voiceMode === 'requesting' ? (
                    <SpinnerIcon className="w-3 h-3 animate-spin" />
                  ) : (
                    <PhoneIcon className="w-3 h-3" />
                  )}
                  <span>
                    {voiceMode === 'requesting'
                      ? 'Connecting...'
                      : (voiceConfig?.buttonText ?? 'Call')}
                  </span>
                </button>
              )}
              {/* During active voice mode, show a small "In call" badge instead */}
              {voiceMode === 'active' && (
                <span
                  className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                  style={{ backgroundColor: '#ef4444' }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block"
                  />
                  In call
                </span>
              )}
              <button
                type="button"
                onClick={closeWidget}
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                aria-label={`Close ${agentName}`}
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Announces call progress and voice errors to assistive tech (#1888). */}
          <p role="status" aria-live="polite" className="sr-only">
            {liveAnnouncement}
          </p>

          {/* ── Voice mode body (replaces message area during active call) ── */}
          {(voiceMode === 'active' || voiceMode === 'requesting' || voiceMode === 'error') && (
            <div className="flex-1 flex flex-col items-center justify-between px-4 py-6 bg-gray-50">
              {voiceMode === 'requesting' && (
                /* Connecting state: spinner + text */
                <div className="flex flex-col items-center justify-center flex-1 gap-4">
                  <SpinnerIcon className="w-10 h-10 animate-spin" style={{ color: theme.accent } as React.CSSProperties} />
                  <p className="text-sm font-semibold text-gray-600">Connecting...</p>
                  <p className="text-xs text-gray-400">Requesting microphone access</p>
                  {/* Text escape while the browser asks for the microphone (#1888). */}
                  {textModeEnabled && (
                    <button
                      type="button"
                      onClick={handleBackToChat}
                      className={`rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors ${FOCUS_RING}`}
                    >
                      Use text instead
                    </button>
                  )}
                </div>
              )}

              {voiceMode === 'error' && (
                /* Error state: exact error message + retry button.
                 * The "back" button behavior differs by mode (Wave 5b):
                 *   shouldVoiceDefault=true  -- user launched from the docked bubble; close
                 *                              the panel entirely (no chat UI was intended).
                 *   shouldVoiceDefault=false -- user clicked "Call" inside the chat panel;
                 *                              dismiss the error and reveal the chat thread. */
                <div className="flex flex-col items-center justify-center flex-1 gap-4 text-center px-2">
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center border border-red-200">
                    <XIcon className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Connection failed</p>
                    {/* Always show the exact error message per the global exact-errors rule. */}
                    <p className="text-xs text-red-600 leading-relaxed">{voiceError}</p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleCallCatalyst()}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 ${FOCUS_RING}`}
                      style={{ backgroundColor: theme.accent }}
                    >
                      Retry
                    </button>
                    {/* Text stays available after every voice failure (#1888). */}
                    {textModeEnabled && (
                      <button
                        type="button"
                        onClick={() => {
                          focusComposerRef.current = true
                          setVoiceMode('idle')
                        }}
                        className={`rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors ${FOCUS_RING}`}
                      >
                        Continue in text
                      </button>
                    )}
                    {/* Voice-default: the visitor came from the docked bubble, so offer to
                        collapse the widget. Voice-only tenants keep their original control. */}
                    {(shouldVoiceDefault || !textModeEnabled) && (
                      <button
                        type="button"
                        onClick={() => {
                          setVoiceMode('idle')
                          if (shouldVoiceDefault) {
                            setIsOpen(false)
                          }
                        }}
                        className={`rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 transition-colors ${FOCUS_RING}`}
                      >
                        {shouldVoiceDefault ? 'Close' : 'Back to chat'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {voiceMode === 'active' && (
                /* Active call state. By default callers see only the orb,
                   transcript, controls, and a small "Troubleshooting" link.
                   Clicking the link expands a panel with mic/output meters,
                   live waveforms, test buttons, and the diagnostic event log. */
                <>
                  <div className="flex-1 flex flex-col items-center justify-center gap-5 w-full overflow-y-auto">
                    <VoiceOrb color={theme.accent} status={voiceStatus} />
                    <VoiceTranscript turns={voiceTranscript} accentColor={theme.accent} />

                    {/* ── Troubleshooting panel (everything diagnostic) ──── */}
                    {debugOpen && (
                      <div className="w-full max-w-[300px] flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                            Troubleshooting
                          </p>
                          <button
                            type="button"
                            onClick={() => setDebugOpen(false)}
                            className="text-[10px] text-gray-400 hover:text-gray-600"
                            aria-label="Close troubleshooting panel"
                          >
                            ×
                          </button>
                        </div>

                        {/* Mic input meter + waveform */}
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between text-[10px] font-medium text-gray-500">
                            <span>You (mic)</span>
                            <span>{micState === 'granted' ? `${micLevel}%` : micState}</span>
                          </div>
                          <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className="h-full transition-all duration-75"
                              style={{
                                width: `${Math.min(100, micLevel)}%`,
                                backgroundColor: micLevel > 60 ? '#22c55e' : micLevel > 20 ? theme.accent : '#9ca3af',
                              }}
                            />
                          </div>
                          <canvas
                            ref={inputWaveformRef}
                            width={280}
                            height={32}
                            className="w-full h-8 rounded bg-gray-50 mt-1"
                            aria-label="Live mic waveform"
                          />
                        </div>

                        {/* Agent output meter + waveform */}
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between text-[10px] font-medium text-gray-500">
                            <span>{agentName} (output) {agentSpeaking ? '🔊' : ''}</span>
                            <span>{outputLevel}%</span>
                          </div>
                          <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className="h-full transition-all duration-75"
                              style={{
                                width: `${Math.min(100, outputLevel)}%`,
                                backgroundColor: outputLevel > 30 ? theme.accent : '#9ca3af',
                              }}
                            />
                          </div>
                          <canvas
                            ref={outputWaveformRef}
                            width={280}
                            height={32}
                            className="w-full h-8 rounded bg-gray-50 mt-1"
                            aria-label="Live agent output waveform"
                          />
                        </div>

                        {/* Test buttons row */}
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={async () => {
                              // Play a brief test tone using a fresh AudioContext owned
                              // by this click handler. The click is a user gesture so
                              // the context is allowed to resume immediately. If the
                              // user hears nothing, their speakers/output device/tab
                              // mute is the culprit -- not anything voice-SDK related.
                              recordVoiceEvent('audit:speaker-test-start', {})
                              try {
                                const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
                                const ctx = new Ctx()
                                if (ctx.state === 'suspended') {
                                  await ctx.resume()
                                  recordVoiceEvent('audit:audioctx-resumed', { state: ctx.state })
                                }
                                const osc = ctx.createOscillator()
                                const gain = ctx.createGain()
                                osc.frequency.value = 800
                                gain.gain.value = 0.05
                                osc.connect(gain)
                                gain.connect(ctx.destination)
                                osc.start()
                                osc.stop(ctx.currentTime + 0.6)
                                osc.onended = () => {
                                  recordVoiceEvent('audit:speaker-test-ended', { ctxState: ctx.state })
                                  void ctx.close()
                                }
                              } catch (err) {
                                recordVoiceEvent('audit:speaker-test-error', {
                                  message: err instanceof Error ? err.message : String(err),
                                })
                              }
                            }}
                            className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 transition-colors"
                            title="Play a brief test beep so you can verify your speakers"
                          >
                            🔊 Test speakers
                          </button>
                          <button
                            type="button"
                            disabled={micTestState !== 'idle'}
                            onClick={() => void runMicRoundtripTest()}
                            className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                            title="Record 3 seconds and play it back so you can verify your microphone"
                          >
                            🎙️ {micTestState === 'recording' ? 'Recording...' : micTestState === 'playing' ? 'Playing...' : 'Test mic'}
                          </button>
                        </div>

                        {/* Live event log */}
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-2 max-h-[160px] overflow-y-auto">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[10px] font-bold text-gray-500 uppercase">
                              Events ({voiceEvents.length})
                            </p>
                            {voiceEvents.length > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  // Copy entire event log as JSON for paste-into-bug-report.
                                  const blob = JSON.stringify(voiceEvents, null, 2)
                                  void navigator.clipboard?.writeText(blob).catch(() => undefined)
                                }}
                                className="text-[10px] text-gray-500 hover:text-gray-700 underline"
                              >
                                Copy log
                              </button>
                            )}
                          </div>
                          {voiceEvents.length === 0 && (
                            <p className="text-[10px] text-gray-400 italic">No events yet.</p>
                          )}
                          {voiceEvents.map((e) => (
                            <div key={e.id} className="text-[10px] font-mono mb-1 leading-tight">
                              <span className="text-gray-400">{e.ts}</span>{' '}
                              <span className="text-blue-700 font-semibold">{e.type}</span>
                              {e.detail && <div className="text-gray-600 break-all pl-3">{e.detail}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Single small "Troubleshooting" link, only visible when the
                        panel is hidden. Keeps the default UI clean and lets
                        users opt-in to diagnostics if they hit a problem. */}
                    {!debugOpen && (
                      <button
                        type="button"
                        onClick={() => setDebugOpen(true)}
                        className="text-[10px] text-gray-400 hover:text-gray-600 underline transition-colors"
                        title="Show speaker/microphone tests and diagnostic logs"
                      >
                        Troubleshooting
                      </button>
                    )}
                  </div>
                  <VoiceControls
                    isMuted={isMuted}
                    onToggleMute={handleToggleMute}
                    onEndCall={handleEndCall}
                    onBackToChat={handleBackToChat}
                    accentColor={theme.accent}
                  />
                </>
              )}
            </div>
          )}

          {/*
           * Hidden container that holds the <elevenlabs-convai> custom element.
           * display:none prevents ElevenLabs' own floating button from appearing.
           * The element is appended here programmatically by handleCallCatalyst().
           */}
          <div ref={convaiContainerRef} style={{ display: 'none' }} aria-hidden="true" />

          {/*
           * Message area + input bar are only shown in idle (chat) mode AND
           * when text mode is enabled. In voice-only mode (textModeEnabled=false)
           * we skip rendering the chat UI entirely so the panel shows only the
           * voice orb + controls.
           */}
          {voiceMode === 'idle' && textModeEnabled && (
            <>
              {/* ── Message area ───────────────────────────────────────── */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
                {messages.map((msg) => msg.kind === 'voice-turn' ? null : msg.kind === 'call-status' ? (
                  <CallStatusRow
                    key={msg.id}
                    status={msg.content}
                    turns={msg.callId ? messages.filter((m) => m.kind === 'voice-turn' && m.callId === msg.callId) : []}
                    agentName={agentName}
                  />
                ) : (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className="max-w-[88%]">
                      {/* Message bubble */}
                      {msg.content && (
                        <div
                          className={`px-3 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                            msg.role === 'user'
                              ? 'text-white rounded-br-sm'
                              : msg.isError
                                ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                                : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm'
                          }`}
                          style={
                            msg.role === 'user'
                              ? { backgroundColor: theme.accent }
                              : undefined
                          }
                        >
                          {msg.id === GREETING_ID ? greetingText : msg.content}
                        </div>
                      )}

                      {/* Retry button for error messages */}
                      {msg.isError && (
                        <button
                          type="button"
                          onClick={() => {
                            // Find the user message before this error
                            const msgIndex = messages.findIndex(
                              (m) => m.id === msg.id
                            )
                            const prevUserMsg =
                              msgIndex > 0
                                ? messages
                                    .slice(0, msgIndex)
                                    .reverse()
                                    .find((m) => m.role === 'user')
                                : null
                            if (prevUserMsg) handleRetry(prevUserMsg.content)
                          }}
                          className="mt-1 text-xs text-red-600 underline hover:no-underline"
                        >
                          Retry
                        </button>
                      )}

                      {/* Source citation chips */}
                      {msg.sources && msg.sources.length > 0 && (
                        <SourceChips sources={msg.sources} theme={theme} />
                      )}

                      {/* Escalation CTA card -- shown below escalated assistant messages */}
                      {msg.isEscalation && activeEscalation && (
                        <EscalationCard
                          trigger={activeEscalation}
                          theme={theme}
                          sessionId={sessionId}
                          messages={messages.map(({ role, content }) => ({
                            role,
                            content,
                          }))}
                          onEmailCaptured={(email) => {
                            setCapturedEmail(email)
                            setCrmSaved(true)
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {/* Streaming indicator: shown when waiting for first delta */}
                {isStreaming &&
                  messages.length > 0 &&
                  messages[messages.length - 1].content === '' && (
                    <div className="flex justify-start">
                      <TypingIndicator theme={theme} />
                    </div>
                  )}

                {/* Inline email capture prompt (after turn 2, before email captured) */}
                {showEmailCapture && !capturedEmail && (
                  <div className="flex justify-start">
                    <div className="max-w-[90%]">
                      <EmailCapturePrompt
                        onSubmit={handleEmailSubmit}
                        onDismiss={() => setShowEmailCapture(false)}
                        theme={theme}
                      />
                    </div>
                  </div>
                )}

                {/* Scroll anchor */}
                <div ref={messagesEndRef} />
              </div>

              {/* ── Input bar ──────────────────────────────────────────── */}
              <div className="flex-shrink-0 border-t border-gray-100 bg-white px-3 py-3">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Ask me anything about ${brand.name}...`}
                    rows={2}
                    maxLength={500}
                    disabled={isStreaming}
                    className="flex-1 resize-none text-sm text-gray-800 placeholder-gray-400 bg-gray-50 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 disabled:opacity-50 overflow-y-hidden"
                    style={{
                      maxHeight: 88,
                      minHeight: 56,
                      // Faint mint-green ring on focus using accent color
                      ['--tw-ring-color' as string]: `${theme.accent}60`,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: `${theme.accent}40`,
                    }}
                    aria-label={`Message ${agentName}`}
                  />
                  <button
                    type="button"
                    onClick={() => void sendMessage()}
                    disabled={isStreaming || !input.trim()}
                    className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                    style={{ backgroundColor: theme.accent }}
                    aria-label="Send"
                  >
                    <SendIcon className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5 text-center">
                  Enter to send &middot; Shift+Enter for new line
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Collapsed pill card (shown when not open) ────────────────────────
          Suppressed entirely when hideLauncher is set: the host page drives the
          widget from its own CTA via catalyst:open(-voice), so no floating
          launcher should appear. The expanded panel + close disc below still
          render once the widget is opened by that event. */}
      {!isOpen && !hideLauncher && (
        <div
          className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-3"
          style={{ width: 260 }}
        >
          {/*
           * Header row (avatar disc + greeting line) intentionally removed — the
           * launcher is now just the action button + attribution so the card is
           * compact. The agent identity is conveyed by the button label itself
           * (e.g. "Talk to Code and Trust"), so the separate logo + "Talk to us"
           * line was redundant.
           */}
          {/*
           * Full-width pill button -- three branches (Wave 5b adds the middle branch):
           *   1. Voice-only mode (modes=['voice']): always go straight to a live call.
           *   2. Voice-default mode (shouldVoiceDefault=true): admin set defaultMode='voice'
           *      in partner config. Bubble uses a mic icon + "Talk to Catalyst" label and
           *      calls handleCallCatalyst() directly. The chat panel briefly renders for
           *      ~150ms while React commits the isOpen=true state before handleCallCatalyst()
           *      transitions voiceMode to 'requesting'. This matches the existing voice-only
           *      Branch 1 pattern and is required because the ConvAI element needs a DOM
           *      container.
           *   3. Chat / both / config-loading: standard "Start a Chat" behavior.
           *      We intentionally fall through to this branch while voiceEnabled===null
           *      (config still loading) to prevent pre-empting into voice mode blind.
           */}
          <div className="px-3 pt-3 pb-3">
            {voiceModeEnabled && !textModeEnabled ? (
              // Branch 1: Voice-only mode prop -- always start a call immediately.
              // This is a static prop-level decision; no config state needed.
              <button
                type="button"
                onClick={() => {
                  openWidget()
                  // Delay slightly so the panel can mount before we trigger the call flow
                  queueVoiceStart()
                }}
                className="w-full rounded-full py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 flex items-center justify-center gap-1.5"
                style={{ backgroundColor: theme.accent }}
                aria-label={`Call ${agentName}`}
              >
                <PhoneIcon className="w-4 h-4" />
                <span>Call {agentName}</span>
              </button>
            ) : shouldVoiceDefault ? (
              // Branch 2: Voice-default mode (Wave 5b).
              // Admin chose defaultMode='voice' in partner config. Clicking the bubble
              // skips the chat panel and launches a live call directly. The icon and
              // label are changed to a microphone + "Talk to Catalyst" so users understand
              // they are initiating a voice call, not opening a text chat. Brand accent
              // color is used (same as the voice-only branch) to keep the visual consistent.
              <button
                type="button"
                onClick={() => {
                  // Open the widget panel first so the voice UI has a container to render into.
                  // The 150ms delay matches the voice-only branch: gives React time to commit
                  // the isOpen=true state before handleCallCatalyst() looks for the ConvAI mount point.
                  openWidget()
                  queueVoiceStart()
                }}
                className="w-full rounded-full py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 flex items-center justify-center gap-1.5"
                style={{ backgroundColor: theme.accent }}
                aria-label={`Talk to ${agentName} via voice`}
              >
                {/* Mic icon signals voice-first intent; same size/padding as the chat button. */}
                <MicIcon className="w-4 h-4 text-white" />
                <span>Talk to {agentName}</span>
              </button>
            ) : (
              // Branch 3: Chat-default or config still loading -- standard behavior.
              // When voiceEnabled===null the config check is still in flight; we fall
              // through here so the bubble never pre-empts into voice mode before we
              // know the config. This is the race-condition safety net documented in
              // the VOICE DEFAULT MODE section of the file header above.
              <button
                type="button"
                onClick={openWidget}
                className="w-full rounded-full py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 flex items-center justify-center gap-1.5"
                style={{ backgroundColor: '#111111' }}
                aria-label={`Start a chat with ${agentName}`}
              >
                <span>💬</span>
                <span>Start a Chat</span>
              </button>
            )}
          </div>

          {/* Attribution -- shows partner name on white-label partner hosts,
              WFW name on direct WFW embeds. The brand name is now an underlined
              link to the brand's canonical site (workforcewave.com for WFW,
              the partner's domain for white-label partners). brand.seo.siteUrl
              is set by the WFW brand config and by mergePartnerBrand() for partners.
              target=_blank + rel=noopener so the host page is preserved when the
              widget is embedded on a third-party site. */}
          <p className="text-[9px] text-gray-300 text-center pb-2">
            Powered by{' '}
            <a
              href={brand.seo.siteUrl || 'https://workforcewave.com'}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-gray-500 focus-visible:outline-2 focus-visible:outline-current focus-visible:outline-offset-1 rounded-sm"
            >
              {brand.name}
            </a>
          </p>
        </div>
      )}

      {/* ── Floating green close disc (only visible when expanded) ──────── */}
      {isOpen && (
        <button
          type="button"
          onClick={closeWidget}
          className="flex items-center justify-center w-10 h-10 rounded-full text-white shadow-md transition-opacity hover:opacity-90"
          style={{ backgroundColor: theme.accent }}
          aria-label={`Close ${agentName}`}
        >
          <XIcon className="w-5 h-5" />
        </button>
      )}

      {/* ── Collapsed open button (FAB when pill card is not shown — never shown simultaneously) */}
      {/* The pill card handles open; this is a safety fallback for deeply embedded contexts */}
    </div>
  )
}
